// infra.js — Infrastructure Ops "Operations" console (Work Order board).
//
// Dashboard KPIs + escalation alert cards, a filterable WO board (with inline
// dependents), and a detail panel (dependency tree, timeline, lifecycle
// actions). All server calls go through api.fetch (cookie auth; throws on !ok)
// against /api/infra/*. DOM is resolved lazily on first show.

import { escapeHtml } from '../../src/lib/format.js';
import { openDialog, closeDialog } from '../../src/lib/dialog.js';
import { badge, titleCase } from '../../src/lib/hr-ui.js';
import { hasRole } from '../../src/lib/roles.js';
import { withButtonBusy } from '../../src/lib/button-busy.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

const API = api.routes;
const flash = (m, t) => notify.showFlash(m, t);

// ---------------------------------------------------------------------------
// Vocab → badge variants
// ---------------------------------------------------------------------------

const STATUS_BADGE = {
  planned: '', scheduled: 'off', assigned: 'info', in_progress: 'warn',
  completed: 'ok', verified: 'ok', closed: 'off', cancelled: 'off',
};
const PRIORITY_BADGE = { emergency: 'warn', unplanned: 'info', planned: 'off' };
// WO type → badge variant (AM-9).
const TYPE_BADGE = { build_out: 'info', corrective_maint: 'warn', preventive_maint: 'violet', site_visit: '' };
const CLOSED = new Set(['completed', 'verified', 'closed', 'cancelled']);

const statusBadge = (s) => badge(titleCase(s || ''), STATUS_BADGE[s] || '');
const priorityBadge = (p) => badge(titleCase(p || ''), PRIORITY_BADGE[p] || '');
const typeBadge = (t) => badge(titleCase(t || ''), TYPE_BADGE[t] || 'off');

function fmtDateTime(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d)) return '—';
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function isOverdue(row) {
  if (!row.due_at || CLOSED.has(row.status)) return false;
  return new Date(row.due_at).getTime() < Date.now();
}

// Whole calendar days past the SLA due date (0 if not overdue).
function tardyDays(row) {
  if (!isOverdue(row)) return 0;
  return Math.floor((Date.now() - new Date(row.due_at).getTime()) / 86400000);
}

// ---------------------------------------------------------------------------
// State + DOM
// ---------------------------------------------------------------------------

let workSets = [];
// Work-set taxonomy CRUD is manager-only (mirrors the backend _REQUIRE_MANAGER
// gate). A supervisor may work with everything else but cannot add/change them.
const canManageWorkSets = () => hasRole('root', 'infra_manager');
let technicians = [];
let adminUsers = [];
let unlinkedUserCount = 0;
let rows = [];
let selectedId = null;
let escalatedOnly = false;
let domReady = false;
let el = {};
let matItems = [];   // material picker catalog [{sku, name}] for name→SKU search

function techName(id) {
  const t = technicians.find(x => x.id === id);
  return t ? t.full_name : null;
}
function workSetName(id) {
  const w = workSets.find(x => x.id === id);
  return w ? w.name : null;
}

function fillSelect(sel, items, { placeholder, valueKey = 'id', labelFn }) {
  if (!sel) return;
  const opts = [placeholder != null ? `<option value="">${escapeHtml(placeholder)}</option>` : '']
    .concat(items.map(i => `<option value="${escapeHtml(i[valueKey])}">${escapeHtml(labelFn(i))}</option>`));
  sel.innerHTML = opts.join('');
}

function resolveDom() {
  if (domReady) return;
  domReady = true;
  const g = (id) => document.getElementById(id);
  el = {
    kpiOpen: g('opsKpiOpen'), kpiOverdue: g('opsKpiOverdue'), kpiUnassigned: g('opsKpiUnassigned'), kpiBlocked: g('opsKpiBlocked'),
    alerts: g('opsAlerts'),
    materialQueueDlg: g('opsMaterialQueueDlg'), materialQueueBody: g('opsMaterialQueueBody'),
    statusChart: g('opsStatusChart'), statusChartBody: g('opsStatusChartBody'),
    statusChartNote: g('opsStatusChartNote'),
    search: g('opsSearch'), statusFilter: g('opsStatusFilter'), typeFilter: g('opsTypeFilter'), workSetFilter: g('opsWorkSetFilter'),
    assigneeFilter: g('opsAssigneeFilter'), overdueFilter: g('opsOverdueFilter'),
    refreshBtn: g('opsRefreshBtn'), newWoBtn: g('opsNewWoBtn'),
    tbody: g('opsTbody'),
    detailEmpty: g('opsDetailEmpty'), detailBody: g('opsDetailBody'),
    detailSubject: g('opsDetailSubject'), detailMeta: g('opsDetailMeta'), detailActions: g('opsDetailActions'),
    detailKv: g('opsDetailKv'), detailDeps: g('opsDetailDeps'), detailTimeline: g('opsDetailTimeline'),
    detailTasks: g('opsDetailTasks'), detailEscalation: g('opsDetailEscalation'),
    detailMaterialRequests: g('opsDetailMaterialRequests'),
    // dialogs
    woDlg: g('opsWoDlg'), woForm: g('opsWoForm'), woSubject: g('opsWoSubject'), woType: g('opsWoType'),
    woPriority: g('opsWoPriority'), woWorkSet: g('opsWoWorkSet'), woAssignee: g('opsWoAssignee'), woDescription: g('opsWoDescription'),
    woFacility: g('opsWoFacility'), woAsset: g('opsWoAsset'), woScheduledDate: g('opsWoScheduledDate'),
    assignDlg: g('opsAssignDlg'), assignForm: g('opsAssignForm'), assignAssignee: g('opsAssignAssignee'), assignOwner: g('opsAssignOwner'),
    blockDlg: g('opsBlockDlg'), blockForm: g('opsBlockForm'), blockReason: g('opsBlockReason'),
    blockDependsField: g('opsBlockDependsField'), blockDependsOn: g('opsBlockDependsOn'), blockDetail: g('opsBlockDetail'), blockEscalate: g('opsBlockEscalate'),
    completeDlg: g('opsCompleteDlg'), completeForm: g('opsCompleteForm'), completeSummary: g('opsCompleteSummary'),
    priorityDlg: g('opsPriorityDlg'), priorityForm: g('opsPriorityForm'), priorityValue: g('opsPriorityValue'), priorityEmergency: g('opsPriorityEmergency'),
    materialsDlg: g('opsMaterialsDlg'), materialsForm: g('opsMaterialsForm'), materialsLines: g('opsMaterialsLines'), materialsAddLine: g('opsMaterialsAddLine'),
  };
  wireEvents();
}

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

async function init() {
  resolveDom();
  try {
    await ensureLookups();
    await Promise.all([loadSummary(), loadList()]);
  } catch (e) {
    flash(e.message || 'Failed to load operations', 'error');
  }
}

let lookupsLoaded = false;
async function ensureLookups() {
  if (lookupsLoaded) return;
  const [ws, techs, facs] = await Promise.all([
    api.fetch(API.infraWorkSets(), { method: 'GET' }),
    api.fetch(API.infraTechnicians(), { method: 'GET' }),
    api.fetch(API.infraFacilities(), { method: 'GET' }).catch(() => ({ items: [] })),
  ]);
  workSets = ws || [];
  technicians = techs || [];
  facilities = (facs && facs.items) || [];
  lookupsLoaded = true;
  fillSelect(el.workSetFilter, workSets, { placeholder: 'All work sets', labelFn: w => w.name });
  fillSelect(el.assigneeFilter, technicians, { placeholder: 'All assignees', labelFn: t => t.full_name });
  fillSelect(el.woWorkSet, workSets, { placeholder: 'Select…', labelFn: w => w.name });
  fillSelect(el.woAssignee, technicians, { placeholder: 'Unassigned', labelFn: t => t.full_name });
  fillSelect(el.woFacility, facilities, { placeholder: '— none —', labelFn: f => f.name });
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

async function loadSummary() {
  const s = await api.fetch(API.infraBoardSummary(), { method: 'GET' });
  el.kpiOpen.textContent = s.open ?? 0;
  el.kpiOverdue.textContent = s.overdue ?? 0;
  el.kpiUnassigned.textContent = s.unassigned ?? 0;
  el.kpiBlocked.textContent = s.blocked ?? 0;
  const alerts = s.alerts || [];
  el.alerts.innerHTML = alerts.length ? alerts.map(a => {
    if (a.type === 'escalation_unrouted') {
      return `<button class="inv-card kpi-out" data-alert-escalations="1" style="text-align:left; cursor:pointer; border:0; ${escalatedOnly ? 'outline:2px solid var(--accent,#2d7);' : ''}">
        <div class="label">⚠ Escalations — no next level</div>
        <div class="value" style="font-size:16px;">${a.count}</div>
        <div class="sub">Unfilled supervisor/manager — ${escalatedOnly ? 'click to clear' : 'click to work the list'}</div>
      </button>`;
    }
    if (a.type === 'escalations') {
      return `<button class="inv-card kpi-low" data-alert-escalations="1" style="text-align:left; cursor:pointer; border:0; ${escalatedOnly ? 'outline:2px solid var(--accent,#2d7);' : ''}">
        <div class="label">⚠ Escalations</div>
        <div class="value" style="font-size:16px;">${a.count} open</div>
        <div class="sub">Climbing the infra chain — ${escalatedOnly ? 'click to clear' : 'click to filter'}</div>
      </button>`;
    }
    if (a.type === 'returns_pending') {
      return `<button class="inv-card kpi-low" data-alert-materials="1" style="text-align:left; cursor:pointer; border:0;">
        <div class="label">↩ Returned stock awaiting approval</div>
        <div class="value" style="font-size:16px;">${a.count}</div>
        <div class="sub">Off the books until someone confirms it arrived</div>
      </button>`;
    }
    if (a.type === 'materials_pending') {
      return `<button class="inv-card kpi-low" data-alert-materials="1" style="text-align:left; cursor:pointer; border:0;">
        <div class="label">📦 Materials awaiting approval</div>
        <div class="value" style="font-size:16px;">${a.count}</div>
        <div class="sub">Supervisor then root — nothing reaches stores until both</div>
      </button>`;
    }
    if (a.type === 'awaiting_verification') {
      return `<button class="inv-card kpi-low" data-alert-status="completed" style="text-align:left; cursor:pointer; border:0; ${el.statusFilter.value === 'completed' ? 'outline:2px solid var(--accent,#2d7);' : ''}">
        <div class="label">✔ Awaiting verification</div>
        <div class="value" style="font-size:16px;">${a.count}</div>
        <div class="sub">Submitted by a technician — click to review and sign off</div>
      </button>`;
    }
    return `<button class="inv-card kpi-out" data-alert-workset="${escapeHtml(a.work_set_id || '')}" style="text-align:left; cursor:pointer; border:0;">
      <div class="label">⚠ Unstaffed work set</div>
      <div class="value" style="font-size:16px;">${escapeHtml(a.label)}</div>
      <div class="sub">${a.count} open WO(s) — no lead supervisor</div>
    </button>`;
  }).join('') : '';
  renderStatusChart(s);
}

// --- Status breakdown ------------------------------------------------------ //

// Pipeline order, not count order: the shape of the bars should read as the
// journey a work order takes, so a pile-up at one stage is obvious.
const STATUS_ORDER = [
  'planned', 'scheduled', 'assigned', 'in_progress',
  'completed', 'verified', 'closed', 'cancelled',
];
const STATUS_LABEL = {
  planned: 'Planned', scheduled: 'Scheduled', assigned: 'Assigned',
  in_progress: 'In progress', completed: 'Completed', verified: 'Verified',
  closed: 'Closed', cancelled: 'Cancelled',
};

function renderStatusChart(s) {
  if (!el.statusChart || !el.statusChartBody) return;
  const counts = s.by_status || {};
  const windowed = new Set(s.windowed_statuses || []);
  const rows = STATUS_ORDER.map(k => ({ key: k, count: Number(counts[k] || 0) }));
  const total = rows.reduce((n, r) => n + r.count, 0);
  if (!total) { el.statusChart.hidden = true; return; }
  el.statusChart.hidden = false;

  const days = Number(s.window_days || 0);
  el.statusChartNote.textContent = days
    ? `Verified, closed and cancelled over the last ${days} days · everything else is current`
    : '';

  const max = Math.max(...rows.map(r => r.count), 1);
  const active = el.statusFilter.value;
  el.statusChartBody.innerHTML = rows.map(({ key, count }) => {
    // A zero bar is still worth showing — an empty "In progress" column is
    // information — but there is nothing to filter to, so it is not clickable.
    const pct = ((count / max) * 100).toFixed(1);
    const flag = key === 'completed' && count > 0 ? '<span class="ops-sb-flag">awaiting verify</span>' : '';
    const win = windowed.has(key) ? ` (last ${days}d)` : '';
    return `<button type="button" class="ops-sb-row${active === key ? ' is-active' : ''}"
      data-status-bar="${key}" ${count ? '' : 'disabled'}
      title="${escapeHtml(STATUS_LABEL[key] || key)}${win}: ${count}">
      <span class="ops-sb-label">${escapeHtml(STATUS_LABEL[key] || key)}</span>
      <span class="ops-sb-track"><span class="ops-sb-fill st-${key}" style="width:${pct}%;"></span></span>
      <span class="ops-sb-count">${count}${flag}</span>
    </button>`;
  }).join('');
}

// ---------------------------------------------------------------------------
// Board list
// ---------------------------------------------------------------------------

// Toggle the board's status filter; clicking the active bar clears it. Reloading
// the summary is what re-draws the active bar and the alert card's outline —
// the same pattern the escalations card uses.
function selectStatus(status) {
  el.statusFilter.value = el.statusFilter.value === status ? '' : status;
  loadSummary();
  loadList();
}

function filterParams() {
  const p = new URLSearchParams();
  if (el.statusFilter.value) p.set('status', el.statusFilter.value);
  if (el.typeFilter?.value) p.set('type', el.typeFilter.value);
  if (el.workSetFilter.value) p.set('work_set_id', el.workSetFilter.value);
  if (el.assigneeFilter.value) p.set('assignee_id', el.assigneeFilter.value);
  if (el.overdueFilter.checked) p.set('overdue', 'true');
  if (escalatedOnly) p.set('escalated', 'true');
  const q = (el.search.value || '').trim();
  if (q) p.set('q', q);
  return p.toString();
}

async function loadList() {
  const data = await api.fetch(API.infraWorkOrders(filterParams()), { method: 'GET' });
  rows = data.items || [];
  renderList();
}

function renderList() {
  if (!rows.length) {
    el.tbody.innerHTML = '<tr><td colspan="7" style="color:var(--sub);">No work orders match.</td></tr>';
    return;
  }
  el.tbody.innerHTML = rows.map(r => {
    const dep = r.dependents_count > 0 ? ` ${badge(`${r.dependents_count} dependent${r.dependents_count > 1 ? 's' : ''}`, 'off')}` : '';
    const blocked = r.blocked_reason ? ` ${badge('blocked', 'warn')}` : '';
    const emergency = r.emergency ? ` ${badge('⚡', 'warn')}` : '';
    const grouped = r.work_group_id ? ` ${badge('grouped', 'violet')}` : '';
    const tardy = tardyDays(r);
    const tardyChip = tardy > 0 ? ` ${badge(`Tardy ${tardy}d`, 'warn')}` : '';
    const due = r.due_at ? `<span${isOverdue(r) ? ' style="color:var(--danger,#c0392b); font-weight:600;"' : ''}>${escapeHtml(fmtDateTime(r.due_at))}</span>${tardyChip}` : '—';
    return `<tr data-wo="${escapeHtml(r.id)}" class="${r.id === selectedId ? 'selected' : ''}" style="cursor:pointer;">
      <td>${escapeHtml(r.subject)}${emergency}${grouped}${dep}${blocked}</td>
      <td>${typeBadge(r.type)}</td>
      <td>${escapeHtml(r.work_set_name || workSetName(r.work_set_id) || '—')}</td>
      <td>${priorityBadge(r.priority)}</td>
      <td>${escapeHtml(r.assignee_name || techName(r.assignee_id) || '—')}</td>
      <td>${due}</td>
      <td>${statusBadge(r.status)}</td>
    </tr>`;
  }).join('');
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

async function selectWo(id) {
  selectedId = id;
  renderList();
  try {
    await ensureChecklistTemplates();
    const wo = await api.fetch(API.infraWorkOrder(id), { method: 'GET' });
    renderDetail(wo);
  } catch (e) {
    flash(e.message || 'Failed to load work order', 'error');
  }
}

function kvRow(label, value) {
  return `<div><span class="k">${escapeHtml(label)}</span><span class="v">${value}</span></div>`;
}

const TASK_BADGE = { pending: '', done: 'ok', failed: 'warn' };
const TASK_LABEL = { pending: 'Pending', done: 'Done', failed: 'Failed' };
// Structural edits (add / edit / reorder / delete / apply template) are supervisor+;
// checking a task off (status) is the executing tech's job too.
const canEditChecklist = () => hasRole('root', 'infra_tech_supervisor', 'infra_manager');
const canExecChecklist = () => hasRole('root', 'infra_tech', 'infra_tech_supervisor', 'infra_manager');

let checklistTemplates = null;   // cached [{id,name,items,...}]

async function ensureChecklistTemplates() {
  if (checklistTemplates !== null) return;
  try {
    checklistTemplates = await api.fetch(API.infraChecklistTemplates(), { method: 'GET' }) || [];
  } catch (e) { checklistTemplates = []; }
}

// Interactive checklist & tasks for the open work order (takes the whole WO).
function renderTasks(wo) {
  const tasks = wo.tasks || [];
  const total = tasks.length;
  const done = tasks.filter(t => t.status === 'done').length;
  const failed = tasks.filter(t => t.status === 'failed').length;
  const pending = total - done - failed;
  const pct = total ? Math.round(done / total * 100) : 0;
  const editable = canEditChecklist();
  const execable = canExecChecklist();

  const header = `<div class="ops-cl-head">
      <span class="ops-cl-progress">${done}/${total} done · ${pct}%</span>
      ${badge(done + ' done', 'ok')} ${badge(pending + ' pending', '')} ${badge(failed + ' failed', 'warn')}
    </div>`;

  let gate;
  if (total === 0) gate = `<div class="ops-cl-gate">No tasks yet — add tasks or apply a template to define what “done” means.</div>`;
  else if (pending === 0) gate = `<div class="ops-cl-gate ok">All tasks resolved — this work order can be completed.</div>`;
  else gate = `<div class="ops-cl-gate warn">${pending} task${pending > 1 ? 's' : ''} pending — each must be <b>done</b> or <b>failed with a note</b> before the WO can be completed.</div>`;

  const rows = tasks.map((t, i) => {
    const mark = t.status === 'done' ? '✓' : (t.status === 'failed' ? '✕' : '');
    const cbx = `<button class="ops-cbx ${t.status}" data-task-toggle="${t.id}" title="Toggle done"${execable ? '' : ' disabled'}>${mark}</button>`;
    const note = t.note ? `<div class="ops-task-note">“${escapeHtml(t.note)}”</div>` : '';
    const acts = [];
    if (execable && t.status !== 'failed') acts.push(`<button class="icon-btn" data-task-fail="${t.id}" title="Mark failed">✕ Fail</button>`);
    if (editable) {
      acts.push(`<button class="icon-btn" data-task-edit="${t.id}" title="Edit">✎</button>`);
      if (i > 0) acts.push(`<button class="icon-btn" data-task-up="${t.id}" title="Move up">↑</button>`);
      if (i < total - 1) acts.push(`<button class="icon-btn" data-task-down="${t.id}" title="Move down">↓</button>`);
      acts.push(`<button class="icon-btn danger" data-task-del="${t.id}" title="Delete">🗑</button>`);
    }
    return `<li class="ops-task ${t.status === 'done' ? 'is-done' : ''}">
        ${cbx}
        <div class="ops-task-body"><div class="ops-task-desc">${escapeHtml(t.description)} ${badge(TASK_LABEL[t.status], TASK_BADGE[t.status])}</div>${note}</div>
        <div class="ops-task-acts">${acts.join('')}</div>
      </li>`;
  }).join('');

  const list = total ? `<ul class="ops-tasks">${rows}</ul>` : '';

  const tplOpts = (checklistTemplates || []).map(t =>
    `<option value="${t.id}">${escapeHtml(t.name)} (${(t.items || []).length})</option>`).join('');
  const tpl = (editable && tplOpts)
    ? `<div class="ops-cl-tpl"><select data-tpl-select><option value="">Apply template…</option>${tplOpts}</select><button class="btn sm" data-tpl-apply>Apply</button></div>` : '';

  const composer = editable
    ? `<div class="ops-cl-add"><input type="text" data-task-input placeholder="Add a task and press Enter"><button class="btn sm primary" data-task-add>Add</button></div>` : '';

  return header + gate + list + tpl + composer;
}

// --- Materials for this work order ------------------------------------------ //
//
// Two lists, because a request now has two lives. It is first an Infra-Ops
// request working through the supervisor → root gate, and only after that an
// Inventory stock request being approved, reserved and delivered. Showing only
// the second would make a freshly-raised request look like it vanished.

const MR_BADGE = {
  approved: 'ok', fulfilled: 'ok', delivered: 'ok', closed: 'ok',
  partially_approved: 'warn', rejected: 'warn', cancelled: 'warn',
};
const APPROVAL_BADGE = {
  pending_supervisor: 'warn', pending_root: 'warn',
  forwarded: 'ok', rejected: 'off', cancelled: 'off',
};
const AWAITING_LABEL = {
  supervisor: 'Waiting on the work-set supervisor',
  root: 'Waiting on root — final approval before stores',
};

function approvalLineText(l) {
  const name = l.sku || l.free_text || l.category || 'item';
  return `${l.quantity} × ${name}`;
}

function renderApprovals(rows) {
  const open = (rows || []).filter(r => r.awaiting);
  const decided = (rows || []).filter(r => !r.awaiting && r.status !== 'forwarded');
  if (!open.length && !decided.length) return '';

  const card = (r) => {
    const lines = (r.lines || []).map(l => `<li>${escapeHtml(approvalLineText(l))}</li>`).join('');
    const who = r.requested_by_name ? ` · asked by ${escapeHtml(r.requested_by_name)}` : '';
    const waiting = r.awaiting
      ? `<div class="ops-mr-wait">${escapeHtml(AWAITING_LABEL[r.awaiting] || '')}</div>`
      : '';
    const why = r.rejection_reason
      ? `<div class="ops-mr-wait">${escapeHtml(r.rejection_reason)}</div>` : '';
    // Only render the buttons where the server said this user can act, so we
    // never offer an action that would come back 403.
    const actions = r.can_decide ? `<div class="ops-mr-actions">
        <button class="btn sm" data-mr-approve="${escapeHtml(r.id)}">Approve</button>
        <button class="btn sm ghost" data-mr-reject="${escapeHtml(r.id)}">Reject</button>
      </div>` : '';
    return `<div class="ops-mr">
        <div>${badge(titleCase(String(r.status).replace(/_/g, ' ')), APPROVAL_BADGE[r.status] || '')}
          <span style="color:var(--sub);">${escapeHtml(r.created_at ? fmtDateTime(r.created_at) : '')}${who}</span></div>
        ${waiting}${why}
        <ul class="ops-mr-lines">${lines}</ul>
        ${actions}
      </div>`;
  };

  return `<div class="ops-mr-group">
      <div class="ops-mr-heading">Awaiting approval</div>
      ${open.length ? open.map(card).join('') : '<span style="color:var(--sub);">None.</span>'}
      ${decided.length ? `<div class="ops-mr-heading">Not approved</div>${decided.map(card).join('')}` : ''}
    </div>`;
}

function renderMaterialRequests(rows) {
  if (!rows || !rows.length) return '<span style="color:var(--sub);">Nothing at stores yet.</span>';
  return rows.map((r) => {
    const lines = (r.lines || []).map((l) => {
      const name = l.item_name || l.free_text || l.category || l.item_sku || 'item';
      const bits = [`${l.qty_requested} requested`];
      if (Number(l.qty_approved) > 0) bits.push(`${l.qty_approved} approved`);
      if (Number(l.qty_fulfilled) > 0) bits.push(`${l.qty_fulfilled} fulfilled`);
      return `<li>${escapeHtml(String(name))} <span style="color:var(--sub);">— ${escapeHtml(bits.join(' · '))}</span></li>`;
    }).join('');
    const status = String(r.status || '');
    const dn = r.delivery_note_number ? ` <span style="color:var(--sub);">DN ${escapeHtml(r.delivery_note_number)}</span>` : '';
    return `<div class="ops-mr">
        <div>${badge(titleCase(status.replace(/_/g, ' ')), MR_BADGE[status] || '')}
          <span style="color:var(--sub);">${escapeHtml(r.created_at ? fmtDateTime(r.created_at) : '')}</span>${dn}</div>
        <ul class="ops-mr-lines">${lines}</ul>
      </div>`;
  }).join('');
}

async function loadMaterialRequests(woId) {
  const box = el.detailMaterialRequests;
  if (!box || !woId) return;
  box.innerHTML = '<span style="color:var(--sub);">Loading…</span>';
  // Independently settled: the gate queue is an Infra-Ops read and the stores
  // list an Inventory one, and a failure in either should not blank the other.
  const [approvals, atStores, returns] = await Promise.allSettled([
    api.fetch(API.infraMaterialRequests(`work_order_id=${encodeURIComponent(woId)}`), { method: 'GET' }),
    api.fetch(API.infraWorkOrderMaterialRequests(woId), { method: 'GET' }),
    // Not every console user may read returns (it is an inventory-approver
    // surface), so a rejection here just hides the group.
    api.fetch(API.infraMaterialReturns(`work_order_id=${encodeURIComponent(woId)}`), { method: 'GET' }),
  ]);
  if (approvals.status === 'rejected' && atStores.status === 'rejected') {
    box.innerHTML = '<span style="color:var(--sub);">Couldn’t load materials.</span>';
    return;
  }
  const approvalHtml = approvals.status === 'fulfilled' ? renderApprovals(approvals.value || []) : '';
  const storesHtml = atStores.status === 'fulfilled'
    ? renderMaterialRequests(atStores.value || [])
    : '<span style="color:var(--sub);">Couldn’t load what is at stores.</span>';
  const returnsHtml = (returns.status === 'fulfilled' && (returns.value || []).length)
    ? `<div class="ops-mr-group"><div class="ops-mr-heading">Returned</div>
        ${returns.value.map((r) => returnCard(r, { decidable: true })).join('')}</div>`
    : '';
  box.innerHTML = `${approvalHtml}<div class="ops-mr-group">
      <div class="ops-mr-heading">At stores</div>${storesHtml}</div>${returnsHtml}`;
}

// Approve / reject inside the gate, from the WO detail panel.
async function onMaterialRequestClick(e) {
  const approve = e.target.closest('[data-mr-approve]');
  const rejectBtn = e.target.closest('[data-mr-reject]');
  const btn = approve || rejectBtn;
  if (!btn) return;
  const id = approve ? approve.dataset.mrApprove : rejectBtn.dataset.mrReject;

  let note = null;
  if (rejectBtn) {
    note = window.prompt('Why is this refused? The requester sees this.');
    if (note === null) return;       // cancelled the prompt
  }
  await withButtonBusy(btn, async () => {
    try {
      if (approve) {
        const body = await api.fetch(API.infraMaterialRequestApprove(id), { method: 'POST' });
        flash(body.status === 'forwarded'
          ? 'Approved and sent to stores'
          : 'Approved — now waiting on root', 'success');
      } else {
        await api.fetch(API.infraMaterialRequestReject(id), {
          method: 'POST', body: JSON.stringify({ note: note || null }),
        });
        flash('Request refused', 'success');
      }
      if (selectedId) loadMaterialRequests(selectedId);
      loadSummary();                 // the pending-approvals card moves with it
    } catch (err) {
      flash(err.message || 'Could not record that decision', 'error');
    }
  });
}

const RETURN_BADGE = { pending_approval: 'warn', received: 'ok', rejected: 'off' };
const RETURN_LABEL = {
  pending_approval: 'Awaiting approval', received: 'Back on hand', rejected: 'Not received',
};

function returnLineText(l) {
  const name = l.item_name || l.sku || 'item';
  return `${l.quantity} × ${name}`;
}

// A return's lines are decided one at a time, because each carries its own
// Inventory approval — so the buttons live on the line, not the header.
function returnCard(r, { decidable = false } = {}) {
  const who = r.declared_by_name ? ` · handed back by ${escapeHtml(r.declared_by_name)}` : '';
  const lines = (r.lines || []).map((l) => {
    const chip = badge(RETURN_LABEL[l.status] || l.status, RETURN_BADGE[l.status] || '');
    const actions = (decidable && l.status === 'pending_approval') ? `
      <span class="ops-mr-actions">
        <button class="btn sm" data-ret-approve="${escapeHtml(r.id)}" data-line="${escapeHtml(l.id)}">Received</button>
        <button class="btn sm ghost" data-ret-reject="${escapeHtml(r.id)}" data-line="${escapeHtml(l.id)}">Didn’t arrive</button>
      </span>` : '';
    return `<li>${escapeHtml(returnLineText(l))} ${chip}${actions}</li>`;
  }).join('');
  return `<div class="ops-mr">
      <div class="ops-mq-wo">${escapeHtml(r.work_order_subject || 'Work order')}</div>
      <div style="color:var(--sub); font-size:12px;">${escapeHtml(r.created_at ? fmtDateTime(r.created_at) : '')}${who}</div>
      <ul class="ops-mr-lines">${lines}</ul>
    </div>`;
}

// Approve / refuse one returned line. "Received" posts the receive that brings
// the stock back on hand; "Didn't arrive" records that it never did, which frees
// the quantity to be declared again.
async function onReturnDecisionClick(e, { refresh }) {
  const approve = e.target.closest('[data-ret-approve]');
  const rejectBtn = e.target.closest('[data-ret-reject]');
  const btn = approve || rejectBtn;
  if (!btn) return;
  const id = approve ? approve.dataset.retApprove : rejectBtn.dataset.retReject;
  const lineId = btn.dataset.line;

  let note = null;
  if (rejectBtn) {
    note = window.prompt('Why was this not received? Kept on the work order.');
    if (note === null) return;
  }
  await withButtonBusy(btn, async () => {
    try {
      await api.fetch(API.infraMaterialReturnDecide(id), {
        method: 'POST',
        body: JSON.stringify({ line_id: lineId, approved: Boolean(approve), note: note || null }),
      });
      flash(approve ? 'Stock is back on hand' : 'Recorded as not received', 'success');
      await refresh();
      loadSummary();
    } catch (err) {
      flash(err.message || 'Could not record that decision', 'error');
    }
  });
}

// The whole approval queue, across every work order — reached from the dashboard
// card, because a request nobody looks at simply never gets ordered.
async function openMaterialQueue() {
  if (!el.materialQueueDlg || !el.materialQueueBody) return;
  el.materialQueueBody.innerHTML = '<span style="color:var(--sub);">Loading…</span>';
  openDialog(el.materialQueueDlg);
  await refreshMaterialQueue();
}

// Requests in and returns out are both "materials waiting on someone", so they
// share one dialog — but they are different acts with different approvers, hence
// separate sections rather than one merged list.
async function refreshMaterialQueue() {
  const [requests, returns] = await Promise.allSettled([
    api.fetch(API.infraMaterialRequests(), { method: 'GET' }),
    api.fetch(API.infraMaterialReturns(), { method: 'GET' }),
  ]);
  if (requests.status === 'rejected' && returns.status === 'rejected') {
    el.materialQueueBody.innerHTML = '<span class="ops-mq-empty">Couldn’t load the queue.</span>';
    return;
  }
  renderMaterialQueue(
    requests.status === 'fulfilled' ? (requests.value || []) : [],
    returns.status === 'fulfilled' ? (returns.value || []) : [],
  );
}

function renderMaterialQueue(rows, returns = []) {
  // Stage order, not request order: what a supervisor can clear now is separate
  // from what only root can, and returns are a third pile with a third approver.
  const groups = [
    ['Waiting on a supervisor', rows.filter(r => r.awaiting === 'supervisor')],
    ['Waiting on root', rows.filter(r => r.awaiting === 'root')],
  ];
  const requestHtml = groups.map(([title, items]) => {
    if (!items.length) return '';
    return `<div class="ops-mr-group">
        <div class="ops-mr-heading">${escapeHtml(title)} (${items.length})</div>
        ${items.map(queueCard).join('')}
      </div>`;
  }).join('');
  const returnHtml = returns.length ? `<div class="ops-mr-group">
      <div class="ops-mr-heading">Returned stock awaiting approval (${returns.length})</div>
      ${returns.map((r) => returnCard(r, { decidable: true })).join('')}
    </div>` : '';
  el.materialQueueBody.innerHTML = (requestHtml + returnHtml)
    || '<span class="ops-mq-empty">Nothing is waiting for approval.</span>';
}

function queueCard(r) {
  const lines = (r.lines || []).map(l => `<li>${escapeHtml(approvalLineText(l))}</li>`).join('');
  const who = r.requested_by_name ? ` · asked by ${escapeHtml(r.requested_by_name)}` : '';
  const actions = r.can_decide ? `<div class="ops-mr-actions">
      <button class="btn sm" data-mq-approve="${escapeHtml(r.id)}">Approve</button>
      <button class="btn sm ghost" data-mq-reject="${escapeHtml(r.id)}">Reject</button>
    </div>` : '<div class="ops-mr-wait">Not yours to decide</div>';
  return `<div class="ops-mr">
      <div class="ops-mq-wo">${escapeHtml(r.work_order_subject || 'Work order')}</div>
      <div style="color:var(--sub); font-size:12px;">${escapeHtml(r.created_at ? fmtDateTime(r.created_at) : '')}${who}</div>
      <ul class="ops-mr-lines">${lines}</ul>
      ${actions}
    </div>`;
}

async function onMaterialQueueClick(e) {
  const approve = e.target.closest('[data-mq-approve]');
  const rejectBtn = e.target.closest('[data-mq-reject]');
  const btn = approve || rejectBtn;
  if (!btn) return;
  const id = approve ? approve.dataset.mqApprove : rejectBtn.dataset.mqReject;

  let note = null;
  if (rejectBtn) {
    note = window.prompt('Why is this refused? The requester sees this.');
    if (note === null) return;
  }
  await withButtonBusy(btn, async () => {
    try {
      if (approve) {
        const body = await api.fetch(API.infraMaterialRequestApprove(id), { method: 'POST' });
        flash(body.status === 'forwarded'
          ? 'Approved and sent to stores'
          : 'Approved — now waiting on root', 'success');
      } else {
        await api.fetch(API.infraMaterialRequestReject(id), {
          method: 'POST', body: JSON.stringify({ note: note || null }),
        });
        flash('Request refused', 'success');
      }
      await refreshMaterialQueue();
      loadSummary();
      if (selectedId) loadMaterialRequests(selectedId);
    } catch (err) {
      flash(err.message || 'Could not record that decision', 'error');
    }
  });
}

// Task/checklist actions (delegated on the checklist container). Refreshes the WO.
async function onChecklistClick(e) {
  const woId = selectedId;
  if (!woId) return;
  let m;
  const post = (url, body) => api.fetch(url, { method: 'POST', body: JSON.stringify(body || {}) });
  try {
    if ((m = e.target.closest('[data-task-toggle]'))) {
      const t = (currentDetail?.tasks || []).find(x => x.id === m.dataset.taskToggle);
      const next = (t && t.status === 'done') ? 'pending' : 'done';
      await post(API.infraWorkOrderTaskStatus(woId, m.dataset.taskToggle), { status: next });
    } else if ((m = e.target.closest('[data-task-fail]'))) {
      const note = window.prompt('Why did this task fail? (required)', '');
      if (!note || !note.trim()) return;
      await post(API.infraWorkOrderTaskStatus(woId, m.dataset.taskFail), { status: 'failed', note: note.trim() });
    } else if ((m = e.target.closest('[data-task-edit]'))) {
      const t = (currentDetail?.tasks || []).find(x => x.id === m.dataset.taskEdit);
      const desc = window.prompt('Edit task', t ? t.description : '');
      if (!desc || !desc.trim()) return;
      await api.fetch(API.infraWorkOrderTask(woId, m.dataset.taskEdit), { method: 'PATCH', body: JSON.stringify({ description: desc.trim() }) });
    } else if ((m = e.target.closest('[data-task-del]'))) {
      if (!window.confirm('Delete this task?')) return;
      await api.fetch(API.infraWorkOrderTask(woId, m.dataset.taskDel), { method: 'DELETE' });
    } else if ((m = e.target.closest('[data-task-up]')) || (m = e.target.closest('[data-task-down]'))) {
      const up = m.hasAttribute('data-task-up');
      const id = up ? m.dataset.taskUp : m.dataset.taskDown;
      const ids = (currentDetail?.tasks || []).map(x => x.id);
      const idx = ids.indexOf(id); const swap = up ? idx - 1 : idx + 1;
      if (idx < 0 || swap < 0 || swap >= ids.length) return;
      [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
      await post(API.infraWorkOrderTasksReorder(woId), { order: ids });
    } else if ((m = e.target.closest('[data-task-add]'))) {
      const input = el.detailTasks.querySelector('[data-task-input]');
      const v = input && input.value.trim(); if (!v) return;
      await post(API.infraWorkOrderTasks(woId), { description: v });
    } else if ((m = e.target.closest('[data-tpl-apply]'))) {
      const sel = el.detailTasks.querySelector('[data-tpl-select]');
      const tid = sel && sel.value; if (!tid) return;
      await post(API.infraWorkOrderApplyChecklist(woId), { template_id: tid });
    } else {
      return;
    }
    await selectWo(woId);
  } catch (err) {
    flash(api.formatErrorDetail(err && err.body && err.body.detail) || (err && err.message) || 'Action failed', 'error');
  }
}

let countdownTimer = null;
function stopCountdown() { if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = null; } }
function startCountdown(dueIso) {
  stopCountdown();
  const node = document.getElementById('opsSlaCountdown');
  if (!node || !dueIso) return;
  const tick = () => {
    const diff = new Date(dueIso).getTime() - Date.now();
    const overdue = diff < 0;
    const secs = Math.abs(diff) / 1000;
    const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60);
    node.textContent = overdue ? `Overdue by ${h}h ${m}m` : `${h}h ${m}m remaining`;
    node.style.color = overdue ? 'var(--danger,#c0392b)' : '';
    node.style.fontWeight = '600';
  };
  tick();
  countdownTimer = setInterval(tick, 30000);
}

function renderEscalationPanel(wo) {
  if (!wo.blocked_reason && !wo.escalation) return '';
  const parts = [];
  if (wo.blocked_reason) parts.push(`<div>Reason: ${badge(titleCase(wo.blocked_reason), 'warn')} ${escapeHtml(wo.blocked_detail || '')}</div>`);
  if (wo.escalation) {
    const e = wo.escalation;
    const target = e.target_technician_id ? techName(e.target_technician_id) : null;
    let routed = `Routed to: ${badge(titleCase(e.level || '—'), 'info')}`;
    if (target) routed += ` ${escapeHtml(target)}`;
    if (e.unrouted) routed += ` ${badge('next level unfilled', 'warn')}`;
    parts.push(`<div>${routed}</div>`);
  }
  parts.push('<div>SLA: <span id="opsSlaCountdown"></span></div>');
  return `<div style="border:1px solid var(--border-soft); border-radius:8px; padding:8px 10px; background:var(--surface-2,rgba(0,0,0,0.03)); font-size:13px;">
    <strong>⚠ Escalation</strong>${parts.join('')}</div>`;
}

// Recursive descendants tree — indented, clickable, status-badged.
function renderTreeNodes(nodes) {
  if (!nodes || !nodes.length) return '';
  return '<ul style="margin:2px 0 0 14px; padding:0; list-style:none;">' + nodes.map(n => {
    const w = n.work_order;
    return `<li style="margin:2px 0;"><span style="color:var(--sub);">▸</span> <a href="#" data-wo-link="${escapeHtml(w.id)}">${escapeHtml(w.subject)}</a> ${statusBadge(w.status)}${renderTreeNodes(n.children)}</li>`;
  }).join('') + '</ul>';
}

function renderDetail(wo) {
  currentDetail = wo;   // task actions read the current tasks/order from here
  el.detailEmpty.hidden = true;
  el.detailBody.hidden = false;
  el.detailSubject.textContent = wo.subject;
  el.detailMeta.innerHTML = `${typeBadge(wo.type)} ${statusBadge(wo.status)} ${priorityBadge(wo.priority)}${wo.emergency ? ' ' + badge('⚡ emergency', 'warn') : ''}`;

  const kv = [];
  kv.push(kvRow('Work set', escapeHtml(wo.work_set_name || workSetName(wo.work_set_id) || '—')));
  kv.push(kvRow('Assignee', escapeHtml(wo.assignee_name || techName(wo.assignee_id) || '—')));
  if (wo.facility_name) kv.push(kvRow('Facility', escapeHtml(wo.facility_name)));
  kv.push(kvRow('Due', `<span${isOverdue(wo) ? ' style="color:var(--danger,#c0392b); font-weight:600;"' : ''}>${escapeHtml(fmtDateTime(wo.due_at))}</span>`));
  if (wo.blocked_reason) kv.push(kvRow('Blocked', `${badge(titleCase(wo.blocked_reason), 'warn')} ${escapeHtml(wo.blocked_detail || '')}`));
  if (wo.quality_ai) {
    const qa = wo.quality_ai;
    let v = (qa.status === 'evaluated' && qa.score != null) ? `${escapeHtml(qa.score)}/100` : badge(titleCase(qa.status || '—'), 'off');
    if (qa.flag_count) v += ` ${badge(`${qa.flag_count} flag${qa.flag_count > 1 ? 's' : ''}`, 'warn')}`;
    if (qa.summary) v += ` <span style="color:var(--sub);">${escapeHtml(qa.summary)}</span>`;
    if (qa.decision) v += ` ${badge(qa.decision, qa.decision === 'approved' ? 'ok' : 'warn')}`;
    kv.push(kvRow('Quality (AI)', v));
  }
  if (wo.description) kv.push(kvRow('Description', escapeHtml(wo.description)));
  if (wo.output_summary) kv.push(kvRow('Output', escapeHtml(wo.output_summary)));
  el.detailKv.innerHTML = kv.join('');

  // Escalation panel with a live SLA countdown (AM-13), shown when blocked/escalated.
  el.detailEscalation.innerHTML = renderEscalationPanel(wo);
  if (wo.blocked_reason || wo.escalation) startCountdown(wo.due_at); else stopCountdown();

  // Checklist / tasks.
  el.detailTasks.innerHTML = renderTasks(wo);
  loadMaterialRequests(wo.id);

  // Dependency tree — ancestor chain (what it waits on) + nested descendants.
  const parts = [];
  if (wo.ancestors && wo.ancestors.length) {
    parts.push('<div style="margin-bottom:6px;">Waits on: ' +
      wo.ancestors.map(a => `<a href="#" data-wo-link="${escapeHtml(a.id)}">${escapeHtml(a.subject)}</a> ${statusBadge(a.status)}`).join(' <span style="color:var(--sub);">→</span> ') +
      '</div>');
  }
  if (wo.descendants && wo.descendants.length) {
    parts.push('<div>Blocks:</div>' + renderTreeNodes(wo.descendants));
  }
  el.detailDeps.innerHTML = parts.length ? parts.join('') : '<span style="color:var(--sub);">No dependencies.</span>';

  // Timeline
  const meta = wo.meta || {};
  const events = [
    ['Created', wo.created_at],
    ['Scheduled', wo.scheduled_date],
    ['Accepted', meta.accepted_at],
    ['On site', meta.on_site_at],
    ['Escalated', wo.escalated_at],
    ['Completed', wo.completed_at],
  ].filter(([, v]) => v);
  el.detailTimeline.innerHTML = events.length
    ? '<ul style="margin:0; padding-left:16px;">' + events.map(([k, v]) => `<li><strong>${escapeHtml(k)}:</strong> ${escapeHtml(fmtDateTime(v))}</li>`).join('') + '</ul>'
    : '<span style="color:var(--sub);">—</span>';

  renderDetailActions(wo);
}

function renderDetailActions(wo) {
  const open = !CLOSED.has(wo.status);
  const btns = [];
  if (['planned', 'scheduled', 'assigned'].includes(wo.status)) btns.push(`<button class="btn" data-act="assign">${wo.assignee_id ? 'Reassign' : 'Assign'}</button>`);
  if (['assigned', 'in_progress'].includes(wo.status)) btns.push('<button class="btn" data-act="start">Start</button>');
  if (wo.status === 'in_progress') btns.push('<button class="btn primary" data-act="complete">Complete</button>');
  if (wo.status === 'completed') {
    btns.push('<button class="btn primary" data-act="approve">Approve quality</button>');
    btns.push('<button class="btn" data-act="rework">Rework</button>');
  }
  if (wo.status === 'verified') btns.push('<button class="btn primary" data-act="close">Close</button>');
  if (open) btns.push('<button class="btn" data-act="priority">Priority</button>');
  if (open) btns.push('<button class="btn" data-act="block">Block</button>');
  if (open) btns.push('<button class="btn" data-act="escalate">Escalate</button>');
  if (open) btns.push('<button class="btn" data-act="materials">Request materials</button>');
  el.detailActions.innerHTML = btns.join(' ');
}

// ---------------------------------------------------------------------------
// Actions / dialogs
// ---------------------------------------------------------------------------

async function refreshAfterAction(detail) {
  await Promise.all([loadSummary(), loadList()]);
  if (detail) renderDetail(detail);
}

function openNewWo() {
  el.woForm.reset();
  fillSelect(el.woWorkSet, workSets, { placeholder: 'Select…', labelFn: w => w.name });
  fillSelect(el.woAssignee, technicians, { placeholder: 'Unassigned', labelFn: t => t.full_name });
  fillSelect(el.woFacility, facilities, { placeholder: '— none —', labelFn: f => f.name });
  el.woAsset.innerHTML = '<option value="">— none —</option>';
  openDialog(el.woDlg);
}

async function loadWoAssets() {
  // Populate the asset picker from the chosen facility's linked assets.
  el.woAsset.innerHTML = '<option value="">— none —</option>';
  if (!el.woFacility.value) return;
  try {
    const f = await api.fetch(API.infraFacility(el.woFacility.value), { method: 'GET' });
    (f.assets || []).forEach(a => {
      const opt = document.createElement('option');
      opt.value = a.unit_id;
      opt.textContent = `${a.name} (${a.serial})`;
      el.woAsset.appendChild(opt);
    });
  } catch { /* no assets / no access — leave 'none' */ }
}

async function submitNewWo(ev) {
  ev.preventDefault();
  const body = {
    type: el.woType.value,
    subject: el.woSubject.value.trim(),
    work_set_id: el.woWorkSet.value,
    priority: el.woPriority.value,
    description: el.woDescription.value.trim() || null,
    facility_id: el.woFacility.value || null,
    asset_id: el.woAsset.value || null,
    scheduled_date: el.woScheduledDate.value || null,
  };
  if (!body.subject || !body.work_set_id) { flash('Subject and work set are required', 'error'); return; }
  if (el.woAssignee.value) { body.owner_id = el.woAssignee.value; body.assignee_id = el.woAssignee.value; }
  try {
    const wo = await api.fetch(API.infraWorkOrders(), { method: 'POST', body: JSON.stringify(body) });
    closeDialog(el.woDlg);
    flash('Work order created', 'success');
    selectedId = wo.id;
    await refreshAfterAction(wo);
  } catch (e) { flash(e.message || 'Create failed', 'error'); }
}

function openAssign(wo) {
  fillSelect(el.assignAssignee, technicians, { placeholder: 'Select…', labelFn: t => t.full_name });
  fillSelect(el.assignOwner, technicians, { placeholder: 'Select…', labelFn: t => t.full_name });
  if (wo.assignee_id) el.assignAssignee.value = wo.assignee_id;
  if (wo.owner_id) el.assignOwner.value = wo.owner_id;
  el.assignForm.dataset.woId = wo.id;
  openDialog(el.assignDlg);
}

async function submitAssign(ev) {
  ev.preventDefault();
  const id = el.assignForm.dataset.woId;
  const body = { assignee_id: el.assignAssignee.value, owner_id: el.assignOwner.value || el.assignAssignee.value };
  if (!body.assignee_id) { flash('Pick an assignee', 'error'); return; }
  try {
    const wo = await api.fetch(API.infraWorkOrderAssign(id), { method: 'POST', body: JSON.stringify(body) });
    closeDialog(el.assignDlg);
    flash('Assigned', 'success');
    await refreshAfterAction(wo);
  } catch (e) { flash(e.message || 'Assign failed', 'error'); }
}

async function doStart(id) {
  try {
    const wo = await api.fetch(API.infraWorkOrderStart(id), { method: 'POST', body: JSON.stringify({}) });
    flash('Started', 'success');
    await refreshAfterAction(wo);
  } catch (e) { flash(e.message || 'Start failed', 'error'); }
}

function openPriority(wo) {
  el.priorityForm.dataset.woId = wo.id;
  el.priorityValue.value = wo.priority;
  el.priorityEmergency.checked = !!wo.emergency;
  openDialog(el.priorityDlg);
}

async function submitPriority(ev) {
  ev.preventDefault();
  const id = el.priorityForm.dataset.woId;
  try {
    const wo = await api.fetch(API.infraWorkOrderPriority(id), {
      method: 'POST', body: JSON.stringify({ priority: el.priorityValue.value, emergency: el.priorityEmergency.checked }),
    });
    closeDialog(el.priorityDlg);
    flash('Priority updated', 'success');
    await refreshAfterAction(wo);
  } catch (e) { flash(e.message || 'Update failed', 'error'); }
}

async function doEscalate(id) {
  try {
    const wo = await api.fetch(API.infraWorkOrderEscalate(id), { method: 'POST', body: JSON.stringify({}) });
    flash('Escalated up the infra chain', 'success');
    await refreshAfterAction(wo);
  } catch (e) { flash(e.message || 'Escalate failed', 'error'); }
}

async function doReview(id, decision) {
  try {
    const wo = await api.fetch(API.infraWorkOrderReviewQuality(id), { method: 'POST', body: JSON.stringify({ decision }) });
    flash(decision === 'approve' ? 'Approved & verified' : 'Sent for rework', 'success');
    await refreshAfterAction(wo);
  } catch (e) { flash(e.message || 'Review failed', 'error'); }
}

async function doClose(id) {
  try {
    const wo = await api.fetch(API.infraWorkOrderClose(id), { method: 'POST', body: JSON.stringify({}) });
    flash('Work order closed', 'success');
    await refreshAfterAction(wo);
  } catch (e) { flash(e.message || 'Close failed', 'error'); }
}

function openComplete(wo) {
  el.completeForm.reset();
  el.completeForm.dataset.woId = wo.id;
  openDialog(el.completeDlg);
}

async function submitComplete(ev) {
  ev.preventDefault();
  const id = el.completeForm.dataset.woId;
  try {
    const wo = await api.fetch(API.infraWorkOrderComplete(id), { method: 'POST', body: JSON.stringify({ output_summary: el.completeSummary.value.trim() || null }) });
    closeDialog(el.completeDlg);
    flash('Completed', 'success');
    await refreshAfterAction(wo);
  } catch (e) { flash(e.message || 'Complete failed', 'error'); }
}

function openBlock(wo) {
  el.blockForm.reset();
  el.blockForm.dataset.woId = wo.id;
  // Depends-on options = other open WOs.
  const others = rows.filter(r => r.id !== wo.id);
  fillSelect(el.blockDependsOn, others, { placeholder: '—', labelFn: r => r.subject });
  el.blockDependsField.hidden = el.blockReason.value !== 'dependency';
  openDialog(el.blockDlg);
}

async function submitBlock(ev) {
  ev.preventDefault();
  const id = el.blockForm.dataset.woId;
  const body = {
    reason: el.blockReason.value,
    detail: el.blockDetail.value.trim() || null,
    escalate: el.blockEscalate.checked,
  };
  if (el.blockReason.value === 'dependency' && el.blockDependsOn.value) body.depends_on_work_order_id = el.blockDependsOn.value;
  try {
    const wo = await api.fetch(API.infraWorkOrderBlock(id), { method: 'POST', body: JSON.stringify(body) });
    closeDialog(el.blockDlg);
    flash('Blocked', 'success');
    await refreshAfterAction(wo);
  } catch (e) { flash(e.message || 'Block failed', 'error'); }
}

function materialLineRow() {
  const div = document.createElement('div');
  div.className = 'ops-mat-line';
  div.style.cssText = 'display:grid; grid-template-columns:96px 1fr 90px 28px; gap:8px; align-items:center;';
  div.innerHTML = `
    <select class="ops-mat-kind"><option value="sku">SKU</option><option value="category">Category</option></select>
    <div class="ops-mat-fields" style="display:flex; gap:6px;"><input type="text" class="ops-mat-sku" list="opsMatItemList" autocomplete="off" placeholder="Search item by name…" style="flex:1; min-width:0;" /></div>
    <input type="number" class="ops-mat-qty" min="0.001" step="0.001" placeholder="Qty" />
    <button type="button" class="btn ghost ops-mat-del" title="Remove">✕</button>`;
  const kind = div.querySelector('.ops-mat-kind');
  const fields = div.querySelector('.ops-mat-fields');
  kind.addEventListener('change', () => {
    fields.innerHTML = kind.value === 'category'
      ? `<input type="text" class="ops-mat-category" placeholder="Category" style="flex:1; min-width:0;" />
         <select class="ops-mat-disp"><option value="consume">Consume</option><option value="deploy">Deploy</option><option value="assign">Assign</option></select>`
      : `<input type="text" class="ops-mat-sku" list="opsMatItemList" autocomplete="off" placeholder="Search item by name…" style="flex:1; min-width:0;" />`;
  });
  div.querySelector('.ops-mat-del').addEventListener('click', () => div.remove());
  return div;
}

// Load the item catalog (infra + tool + consumable stock) into a datalist so the
// SKU inputs are searchable by name — techs won't know SKU numbers. Best-effort.
async function ensureMatItems() {
  let dl = document.getElementById('opsMatItemList');
  if (!dl) {
    dl = document.createElement('datalist');
    dl.id = 'opsMatItemList';
    (el.materialsDlg || document.body).appendChild(dl);
  }
  if (matItems.length) return;
  try {
    // Infra-scoped catalog (infra roles have no inventory-console access).
    matItems = await api.fetch(API.infraMaterialItems(), { method: 'GET' }) || [];
    dl.innerHTML = matItems
      .map(i => `<option value="${escapeHtml(i.name)} — ${escapeHtml(i.sku)}"></option>`)
      .join('');
  } catch { /* inventory off — free-text SKU still allowed */ }
}

// Resolve typed/picked text (name, "Name — SKU", or a raw SKU) back to a SKU.
function resolveMatSku(text) {
  const t = (text || '').trim();
  if (!t) return '';
  const lower = t.toLowerCase();
  const disp = matItems.find(i => `${i.name} — ${i.sku}`.toLowerCase() === lower);
  if (disp) return disp.sku;
  const byName = matItems.filter(i => String(i.name || '').toLowerCase() === lower);
  if (byName.length === 1) return byName[0].sku;
  const bySku = matItems.find(i => String(i.sku || '').toLowerCase() === lower);
  if (bySku) return bySku.sku;
  const dash = t.lastIndexOf('—');
  if (dash >= 0) {
    const tail = t.slice(dash + 1).trim().toLowerCase();
    const m = matItems.find(i => String(i.sku || '').toLowerCase() === tail);
    if (m) return m.sku;
  }
  return t;  // fall back to raw text
}

function openMaterials(wo) {
  el.materialsLines.innerHTML = '';
  ensureMatItems();
  el.materialsLines.appendChild(materialLineRow());
  el.materialsForm.dataset.woId = wo.id;
  openDialog(el.materialsDlg);
}

async function submitMaterials(ev) {
  ev.preventDefault();
  const id = el.materialsForm.dataset.woId;
  const lines = [];
  el.materialsLines.querySelectorAll('.ops-mat-line').forEach(div => {
    const qty = div.querySelector('.ops-mat-qty')?.value;
    if (!(Number(qty) > 0)) return;
    if (div.querySelector('.ops-mat-kind')?.value === 'category') {
      const category = div.querySelector('.ops-mat-category')?.value.trim();
      const disposition = div.querySelector('.ops-mat-disp')?.value;
      if (category) lines.push({ category, disposition, quantity: qty });
    } else {
      const sku = resolveMatSku(div.querySelector('.ops-mat-sku')?.value);
      if (sku) lines.push({ sku, quantity: qty });
    }
  });
  if (!lines.length) { flash('Add at least one line', 'error'); return; }
  try {
    await api.fetch(API.infraWorkOrderRequestMaterials(id), { method: 'POST', body: JSON.stringify({ lines }) });
    closeDialog(el.materialsDlg);
    flash('Stock request submitted', 'success');
  } catch (e) { flash(e.message || 'Request failed', 'error'); }
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

let currentDetail = null;

function wireEvents() {
  el.refreshBtn?.addEventListener('click', () => init());
  el.newWoBtn?.addEventListener('click', openNewWo);
  el.woFacility?.addEventListener('change', loadWoAssets);
  let searchTimer = null;
  el.search?.addEventListener('input', () => { clearTimeout(searchTimer); searchTimer = setTimeout(() => loadList(), 250); });
  [el.statusFilter, el.typeFilter, el.workSetFilter, el.assigneeFilter, el.overdueFilter].forEach(s => s?.addEventListener('change', () => loadList()));
  // Picking a status from the dropdown must move the chart's highlight too, or
  // the two controls disagree about what the board is showing.
  el.statusFilter?.addEventListener('change', () => loadSummary());

  el.tbody?.addEventListener('click', (e) => {
    const tr = e.target.closest('tr[data-wo]');
    if (tr) selectWo(tr.dataset.wo);
  });
  el.alerts?.addEventListener('click', (e) => {
    if (e.target.closest('[data-alert-escalations]')) {
      escalatedOnly = !escalatedOnly;
      loadSummary();  // refresh the card's active outline
      loadList();
      return;
    }
    if (e.target.closest('[data-alert-materials]')) { openMaterialQueue(); return; }
    const status = e.target.closest('[data-alert-status]');
    if (status) { selectStatus(status.dataset.alertStatus); return; }
    const btn = e.target.closest('[data-alert-workset]');
    if (btn && btn.dataset.alertWorkset) { el.workSetFilter.value = btn.dataset.alertWorkset; loadList(); }
  });
  // A bar is a way in to the work: clicking it filters the board, clicking the
  // active one clears back to the open list.
  el.statusChartBody?.addEventListener('click', (e) => {
    const bar = e.target.closest('[data-status-bar]');
    if (bar) selectStatus(bar.dataset.statusBar);
  });
  el.detailMaterialRequests?.addEventListener('click', (e) => { void onMaterialRequestClick(e); });
  el.detailMaterialRequests?.addEventListener('click', (e) => {
    void onReturnDecisionClick(e, { refresh: async () => { if (selectedId) await loadMaterialRequests(selectedId); } });
  });
  el.materialQueueBody?.addEventListener('click', (e) => { void onMaterialQueueClick(e); });
  el.materialQueueBody?.addEventListener('click', (e) => {
    void onReturnDecisionClick(e, { refresh: refreshMaterialQueue });
  });
  el.detailDeps?.addEventListener('click', (e) => {
    const a = e.target.closest('[data-wo-link]');
    if (a) { e.preventDefault(); selectWo(a.dataset.woLink); }
  });
  el.detailActions?.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-act]');
    if (!btn || !selectedId) return;
    const wo = await api.fetch(API.infraWorkOrder(selectedId), { method: 'GET' });
    currentDetail = wo;
    const act = btn.dataset.act;
    if (act === 'assign') openAssign(wo);
    else if (act === 'start') doStart(wo.id);
    else if (act === 'complete') openComplete(wo);
    else if (act === 'approve') doReview(wo.id, 'approve');
    else if (act === 'rework') doReview(wo.id, 'rework');
    else if (act === 'close') doClose(wo.id);
    else if (act === 'priority') openPriority(wo);
    else if (act === 'block') openBlock(wo);
    else if (act === 'escalate') doEscalate(wo.id);
    else if (act === 'materials') openMaterials(wo);
  });

  el.detailTasks?.addEventListener('click', onChecklistClick);
  el.detailTasks?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && e.target.matches('[data-task-input]')) {
      e.preventDefault();
      const addBtn = el.detailTasks.querySelector('[data-task-add]');
      if (addBtn) onChecklistClick({ target: addBtn });
    }
  });

  el.woForm?.addEventListener('submit', submitNewWo);
  el.assignForm?.addEventListener('submit', submitAssign);
  el.blockForm?.addEventListener('submit', submitBlock);
  el.completeForm?.addEventListener('submit', submitComplete);
  el.materialsForm?.addEventListener('submit', submitMaterials);
  el.priorityForm?.addEventListener('submit', submitPriority);
  el.blockReason?.addEventListener('change', () => { el.blockDependsField.hidden = el.blockReason.value !== 'dependency'; });
  el.materialsAddLine?.addEventListener('click', () => el.materialsLines.appendChild(materialLineRow()));

  // dialog dismiss buttons
  document.querySelectorAll('[data-dialog-dismiss]').forEach(b => {
    if (b.dataset.opsWired) return;
    if (['opsWoDlg', 'opsAssignDlg', 'opsBlockDlg', 'opsCompleteDlg', 'opsMaterialsDlg', 'opsPriorityDlg'].includes(b.dataset.dialogDismiss)) {
      b.dataset.opsWired = '1';
      b.addEventListener('click', () => closeDialog(document.getElementById(b.dataset.dialogDismiss)));
    }
  });
}

// ===========================================================================
// TEAM (roster + work sets)
// ===========================================================================

let staffingMap = {};
let teamDomReady = false;
let tel = {};

function resolveTeamDom() {
  if (teamDomReady) return;
  teamDomReady = true;
  const g = (id) => document.getElementById(id);
  tel = {
    techTbody: g('opsTechTbody'), workSetTbody: g('opsWorkSetTbody'), unlinkedCard: g('opsUnlinkedCard'),
    refreshBtn: g('opsTeamRefreshBtn'), newTechBtn: g('opsNewTechBtn'), newWorkSetBtn: g('opsNewWorkSetBtn'),
    techDlg: g('opsTechDlg'), techForm: g('opsTechForm'), techDlgTitle: g('opsTechDlgTitle'),
    techName: g('opsTechName'), techPhone: g('opsTechPhone'), techUserInput: g('opsTechUserInput'), techUserOptions: g('opsTechUserOptions'), techActiveField: g('opsTechActiveField'), techActive: g('opsTechActive'),
    skillsDlg: g('opsSkillsDlg'), skillsForm: g('opsSkillsForm'), skillsTechName: g('opsSkillsTechName'), skillsList: g('opsSkillsList'),
    workSetDlg: g('opsWorkSetDlg'), workSetForm: g('opsWorkSetForm'), workSetDlgTitle: g('opsWorkSetDlgTitle'),
    workSetCode: g('opsWorkSetCode'), workSetName: g('opsWorkSetName'), workSetLead: g('opsWorkSetLead'),
    workSetActiveField: g('opsWorkSetActiveField'), workSetActive: g('opsWorkSetActive'), workSetDeleteBtn: g('opsWorkSetDeleteBtn'),
    gpsInput: g('opsGpsAccuracyInput'), gpsHint: g('opsGpsAccuracyHint'),
    gpsSaveBtn: g('opsGpsAccuracySaveBtn'), gpsClearBtn: g('opsGpsAccuracyClearBtn'),
    fieldSettingsActions: g('opsFieldSettingsActions'),
  };
  wireTeamEvents();
  // Only managers/root may add work sets; supervisors see the list read-only.
  if (tel.newWorkSetBtn) tel.newWorkSetBtn.style.display = canManageWorkSets() ? '' : 'none';
  // Same rule for the field-app threshold: a supervisor may see the bar their
  // crews are held to, but only a manager/root may move it.
  if (!canManageWorkSets()) {
    if (tel.gpsInput) tel.gpsInput.disabled = true;
    if (tel.fieldSettingsActions) tel.fieldSettingsActions.style.display = 'none';
  }
}

async function initTeam() {
  resolveTeamDom();
  try { await loadTeam(); } catch (e) { flash(e.message || 'Failed to load team', 'error'); }
}


async function loadTeam() {
  const [techs, sets, staffing, eligible] = await Promise.all([
    api.fetch(API.infraTechnicians(''), { method: 'GET' }),   // '' → active_only default true; pass active_only=false to include all
    api.fetch(API.infraWorkSets('active_only=false'), { method: 'GET' }),
    api.fetch(API.infraWorkSetStaffing(), { method: 'GET' }),
    // Only infra tech/supervisor accounts may back a technician login.
    api.fetch(API.infraEligibleUsers(), { method: 'GET' }).catch(() => ({ items: [], unlinked_count: 0 })),
  ]);
  technicians = techs || [];
  workSets = sets || [];
  adminUsers = (eligible && eligible.items) || [];
  unlinkedUserCount = (eligible && eligible.unlinked_count) || 0;
  staffingMap = staffing || {};
  lookupsLoaded = true;
  renderTechs();
  renderWorkSets();
  renderUnlinkedCard();
  loadFieldSettings().catch(() => {});
}

function skillNames(ids) {
  return (ids || []).map(id => workSetName(id)).filter(Boolean).join(', ') || '—';
}

function adminUserLabel(u) {
  const name = (u.full_name || '').trim();
  return name ? `${name} (${u.username})` : u.username;
}

function adminUserName(id) {
  if (id == null) return null;
  const u = adminUsers.find(x => Number(x.id) === Number(id));
  return u ? adminUserLabel(u) : `#${id}`;
}

// Datalist options are keyed by the visible label; resolveTechUserId maps the
// typed/selected label back to a user id at submit time. Only unlinked accounts
// are offered (plus the one this technician is already linked to, when editing),
// so you can't pick a login already taken by another technician.
function populateTechUserOptions(currentUserId) {
  const dl = tel.techUserOptions;
  if (!dl) return;
  const avail = adminUsers.filter(u => !u.linked || Number(u.id) === Number(currentUserId));
  dl.innerHTML = avail.map(u => `<option value="${escapeHtml(adminUserLabel(u))}"></option>`).join('');
}

// Small roster card: how many infra tech/supervisor accounts still have no
// technician record — the onboarding gap for future logins.
function renderUnlinkedCard() {
  if (!tel.unlinkedCard) return;
  const n = unlinkedUserCount || 0;
  tel.unlinkedCard.hidden = false;
  tel.unlinkedCard.className = 'inv-card' + (n > 0 ? ' kpi-low' : '');
  tel.unlinkedCard.innerHTML =
    `<div class="label">Unlinked infra accounts</div>` +
    `<div class="value${n ? '' : ' is-zero'}">${n}</div>` +
    `<div class="sub">Tech/supervisor logins not yet on the roster</div>`;
}

function resolveTechUserId() {
  const v = (tel.techUserInput && tel.techUserInput.value || '').trim();
  if (!v) return { id: null, ok: true };
  const u = adminUsers.find(x => adminUserLabel(x) === v);
  return u ? { id: Number(u.id), ok: true } : { id: null, ok: false };
}

function renderTechs() {
  if (!technicians.length) { tel.techTbody.innerHTML = '<tr><td colspan="5" style="color:var(--sub);">No technicians.</td></tr>'; return; }
  tel.techTbody.innerHTML = technicians.map(t => `
    <tr>
      <td>${escapeHtml(t.full_name)}${t.admin_user_id ? `<div style="color:var(--sub); font-size:11px;">🔗 ${escapeHtml(adminUserName(t.admin_user_id))}</div>` : ''}</td>
      <td>${escapeHtml(t.phone || '—')}</td>
      <td style="font-size:12px;">${escapeHtml(skillNames(t.skill_work_set_ids))}</td>
      <td>${t.is_active ? badge('Active', 'ok') : badge('Inactive', 'off')}</td>
      <td style="white-space:nowrap;"><button class="btn ghost" data-tech-edit="${escapeHtml(t.id)}">Edit</button> <button class="btn ghost" data-tech-skills="${escapeHtml(t.id)}">Skills</button></td>
    </tr>`).join('');
}

function renderWorkSets() {
  if (!workSets.length) { tel.workSetTbody.innerHTML = '<tr><td colspan="5" style="color:var(--sub);">No work sets.</td></tr>'; return; }
  const canManage = canManageWorkSets();
  tel.workSetTbody.innerHTML = workSets.map(w => {
    const n = Number(staffingMap[w.id] || 0);
    const staffBadge = badge(`${n} tech${n === 1 ? '' : 's'}`, n > 0 ? 'ok' : 'warn');
    return `<tr>
      <td>${escapeHtml(w.name)} <span style="color:var(--sub); font-size:11px;">${escapeHtml(w.code)}</span></td>
      <td>${escapeHtml(techName(w.lead_technician_id) || '—')}</td>
      <td>${staffBadge}</td>
      <td>${w.is_active ? badge('Active', 'ok') : badge('Inactive', 'off')}</td>
      <td>${canManage ? `<button class="btn ghost" data-workset-edit="${escapeHtml(w.id)}">Edit</button>` : ''}</td>
    </tr>`;
  }).join('');
}

// technician dialogs
function openTech(tech) {
  tel.techForm.reset();
  tel.techForm.dataset.editId = tech ? tech.id : '';
  tel.techDlgTitle.textContent = tech ? 'Edit technician' : 'New technician';
  tel.techActiveField.hidden = !tech;
  populateTechUserOptions(tech ? tech.admin_user_id : null);
  tel.techUserInput.value = (tech && tech.admin_user_id) ? (adminUserName(tech.admin_user_id) || '') : '';
  if (tech) { tel.techName.value = tech.full_name; tel.techPhone.value = tech.phone || ''; tel.techActive.checked = tech.is_active; }
  openDialog(tel.techDlg);
}

async function submitTech(ev) {
  ev.preventDefault();
  const id = tel.techForm.dataset.editId;
  const name = tel.techName.value.trim();
  if (!name) { flash('Name is required', 'error'); return; }
  const picked = resolveTechUserId();
  if (!picked.ok) { flash('Pick a user from the list, or clear the field', 'error'); return; }
  const adminUserId = picked.id;
  try {
    if (id) {
      await api.fetch(API.infraTechnician(id), { method: 'PATCH', body: JSON.stringify({ full_name: name, phone: tel.techPhone.value.trim() || null, admin_user_id: adminUserId, is_active: tel.techActive.checked }) });
    } else {
      await api.fetch(API.infraTechnicians(), { method: 'POST', body: JSON.stringify({ full_name: name, phone: tel.techPhone.value.trim() || null, admin_user_id: adminUserId }) });
    }
    closeDialog(tel.techDlg);
    flash('Saved', 'success');
    await loadTeam();
  } catch (e) { flash(e.message || 'Save failed', 'error'); }
}

function openSkills(tech) {
  tel.skillsForm.dataset.techId = tech.id;
  tel.skillsTechName.textContent = tech.full_name;
  const current = new Set(tech.skill_work_set_ids || []);
  tel.skillsList.innerHTML = workSets.map(w => `<label style="display:flex; align-items:center; gap:8px;"><input type="checkbox" value="${escapeHtml(w.id)}" ${current.has(w.id) ? 'checked' : ''} /> ${escapeHtml(w.name)}</label>`).join('');
  openDialog(tel.skillsDlg);
}

async function submitSkills(ev) {
  ev.preventDefault();
  const id = tel.skillsForm.dataset.techId;
  const ids = Array.from(tel.skillsList.querySelectorAll('input:checked')).map(i => i.value);
  try {
    await api.fetch(API.infraTechnicianSkills(id), { method: 'PUT', body: JSON.stringify({ work_set_ids: ids }) });
    closeDialog(tel.skillsDlg);
    flash('Skills updated', 'success');
    await loadTeam();
  } catch (e) { flash(e.message || 'Save failed', 'error'); }
}

// work-set dialogs
function openWorkSet(ws) {
  if (!canManageWorkSets()) { flash('Only an infra manager can add or change work sets', 'error'); return; }
  tel.workSetForm.reset();
  tel.workSetForm.dataset.editId = ws ? ws.id : '';
  tel.workSetDlgTitle.textContent = ws ? 'Edit work set' : 'New work set';
  tel.workSetActiveField.hidden = !ws;
  tel.workSetDeleteBtn.hidden = !ws;
  tel.workSetCode.disabled = !!ws;  // code is immutable after create
  fillSelect(tel.workSetLead, technicians, { placeholder: 'Unstaffed', labelFn: t => t.full_name });
  if (ws) {
    tel.workSetCode.value = ws.code; tel.workSetName.value = ws.name;
    tel.workSetLead.value = ws.lead_technician_id || ''; tel.workSetActive.checked = ws.is_active;
  }
  openDialog(tel.workSetDlg);
}

async function submitWorkSet(ev) {
  ev.preventDefault();
  const id = tel.workSetForm.dataset.editId;
  try {
    if (id) {
      await api.fetch(API.infraWorkSet(id), { method: 'PATCH', body: JSON.stringify({ name: tel.workSetName.value.trim(), lead_technician_id: tel.workSetLead.value || null, is_active: tel.workSetActive.checked }) });
    } else {
      const code = tel.workSetCode.value.trim(), name = tel.workSetName.value.trim();
      if (!code || !name) { flash('Code and name are required', 'error'); return; }
      await api.fetch(API.infraWorkSets(), { method: 'POST', body: JSON.stringify({ code, name, lead_technician_id: tel.workSetLead.value || null }) });
    }
    closeDialog(tel.workSetDlg);
    flash('Saved', 'success');
    await loadTeam();
  } catch (e) { flash(e.message || 'Save failed', 'error'); }
}

async function deleteWorkSet() {
  const id = tel.workSetForm.dataset.editId;
  if (!id) return;
  try {
    await api.fetch(API.infraWorkSet(id), { method: 'DELETE' });
    closeDialog(tel.workSetDlg);
    flash('Work set deleted', 'success');
    await loadTeam();
  } catch (e) { flash(e.message || 'Delete failed — it may still be in use', 'error'); }
}

// --- Field app settings -----------------------------------------------------
// The GPS accuracy bar the technician app enforces before it will accept a
// submission. Held here so it can change without a new app build; leaving it
// unset hands the value back to the deployment default.

let fieldSettings = null;

function renderFieldSettings() {
  if (!tel.gpsInput || !fieldSettings) return;
  const configured = fieldSettings.gps_accuracy_threshold_configured;
  tel.gpsInput.value = configured == null ? '' : String(configured);
  if (fieldSettings.gps_accuracy_min != null) tel.gpsInput.min = String(fieldSettings.gps_accuracy_min);
  if (fieldSettings.gps_accuracy_max != null) tel.gpsInput.max = String(fieldSettings.gps_accuracy_max);
  if (tel.gpsHint) {
    const dflt = fieldSettings.gps_accuracy_threshold_default;
    const lo = fieldSettings.gps_accuracy_min, hi = fieldSettings.gps_accuracy_max;
    tel.gpsHint.textContent = configured == null
      ? `Unset — technicians are held to the default ${dflt} m. Allowed range ${lo}–${hi} m.`
      : `In force: ≤${fieldSettings.gps_accuracy_threshold} m. Clear it to fall back to ${dflt} m.`;
  }
}

async function loadFieldSettings() {
  if (!tel.gpsInput) return;
  try {
    fieldSettings = await api.fetch(API.infraSettings(), { method: 'GET' });
    renderFieldSettings();
  } catch (e) {
    if (tel.gpsHint) tel.gpsHint.textContent = 'Could not load the field-app threshold.';
  }
}

async function saveGpsAccuracy(clear) {
  if (!tel.gpsInput) return;
  let value = null;
  if (!clear) {
    const raw = (tel.gpsInput.value || '').trim();
    if (!raw) { flash('Enter a threshold in metres, or press “Use default”.', 'error'); return; }
    value = Number(raw);
    if (!Number.isFinite(value)) { flash('Threshold must be a number.', 'error'); return; }
  }
  try {
    fieldSettings = await api.fetch(API.infraSettings(), {
      method: 'PATCH', body: JSON.stringify({ gps_accuracy_threshold: value }),
    });
    renderFieldSettings();
    // Devices pick this up on their next foreground, so say so rather than
    // implying it took effect in the field the instant the button was pressed.
    flash(clear
      ? `Cleared — technicians fall back to ${fieldSettings.gps_accuracy_threshold} m. Devices update when the app is next reopened.`
      : `Saved — ≤${fieldSettings.gps_accuracy_threshold} m. Devices update when the app is next reopened.`);
  } catch (e) {
    flash(e.message || 'Failed to save the threshold', 'error');
    renderFieldSettings();
  }
}

function wireTeamEvents() {
  tel.refreshBtn?.addEventListener('click', () => initTeam());
  tel.newTechBtn?.addEventListener('click', () => openTech(null));
  tel.newWorkSetBtn?.addEventListener('click', () => openWorkSet(null));
  tel.techTbody?.addEventListener('click', (e) => {
    const ed = e.target.closest('[data-tech-edit]'); const sk = e.target.closest('[data-tech-skills]');
    if (ed) openTech(technicians.find(t => t.id === ed.dataset.techEdit));
    else if (sk) openSkills(technicians.find(t => t.id === sk.dataset.techSkills));
  });
  tel.workSetTbody?.addEventListener('click', (e) => {
    const ed = e.target.closest('[data-workset-edit]');
    if (ed) openWorkSet(workSets.find(w => w.id === ed.dataset.worksetEdit));
  });
  tel.techForm?.addEventListener('submit', submitTech);
  tel.skillsForm?.addEventListener('submit', submitSkills);
  tel.workSetForm?.addEventListener('submit', submitWorkSet);
  tel.workSetDeleteBtn?.addEventListener('click', deleteWorkSet);
  tel.gpsSaveBtn?.addEventListener('click', () => void saveGpsAccuracy(false));
  tel.gpsClearBtn?.addEventListener('click', () => void saveGpsAccuracy(true));
  wireDismiss(['opsTechDlg', 'opsSkillsDlg', 'opsWorkSetDlg']);
}

// ===========================================================================
// FACILITIES
// ===========================================================================

let facilities = [];
let facSelectedId = null;
let facDomReady = false;
let fel = {};
// Client-side sort + paging over the set the endpoint returns (server applies the
// type/status/search filters). Default sort is name, ascending.
const FAC_PAGE_SIZE = 30;
const FAC_COLSPAN = 8;
let facPage = 0;
let facSort = { key: 'name', dir: 1 };
// facSelectedId doubles as the id of the row expanded inline (one at a time);
// its fetched detail is cached here so re-paints don't refetch.
const facDetailCache = {};

function resolveFacDom() {
  if (facDomReady) return;
  facDomReady = true;
  const g = (id) => document.getElementById(id);
  fel = {
    search: g('opsFacSearch'), typeFilter: g('opsFacTypeFilter'), statusFilter: g('opsFacStatusFilter'),
    refreshBtn: g('opsFacRefreshBtn'), newBtn: g('opsNewFacBtn'), tbody: g('opsFacTbody'),
    table: g('opsFacTable'), prev: g('opsFacPrev'), next: g('opsFacNext'), pageInfo: g('opsFacPageInfo'),
    dlg: g('opsFacDlg'), form: g('opsFacForm'), dlgTitle: g('opsFacDlgTitle'),
    name: g('opsFacName'), code: g('opsFacCode'), type: g('opsFacType'), status: g('opsFacStatus'),
    lat: g('opsFacLat'), lng: g('opsFacLng'), serviceArea: g('opsFacServiceArea'),
    commissionedAt: g('opsFacCommissionedAt'), address: g('opsFacAddress'),
    importBtn: g('opsFacImportBtn'), importDlg: g('opsFacImportDlg'), importFile: g('opsFacImportFile'),
    importResult: g('opsFacImportResult'), importPreviewBtn: g('opsFacImportPreviewBtn'), importCommitBtn: g('opsFacImportCommitBtn'),
  };
  wireFacEvents();
}

async function initFacilities() {
  resolveFacDom();
  try { await ensureLookups(); await loadFacilities(); } catch (e) { flash(e.message || 'Failed to load facilities', 'error'); }
}

async function loadFacilities() {
  const p = new URLSearchParams();
  if (fel.typeFilter.value) p.set('type', fel.typeFilter.value);
  if (fel.statusFilter.value) p.set('status', fel.statusFilter.value);
  const q = (fel.search.value || '').trim(); if (q) p.set('q', q);
  const data = await api.fetch(API.infraFacilities(p.toString()), { method: 'GET' });
  facilities = data.items || [];
  facPage = 0;
  renderFacilities();
}

const FAC_SORT_VAL = {
  name: f => (f.name || '').toLowerCase(),
  code: f => (f.code || '').toLowerCase(),
  type: f => (f.type || '').toLowerCase(),
  service_area: f => (f.service_area || '').toLowerCase(),
  commissioned_at: f => f.commissioned_at || '',
  asset_count: f => f.asset_count ?? 0,
  open_wo_count: f => f.open_wo_count ?? 0,
  status: f => (f.status || '').toLowerCase(),
};

function sortedFacilities() {
  const val = FAC_SORT_VAL[facSort.key] || FAC_SORT_VAL.name;
  return facilities.slice().sort((a, b) => {
    const av = val(a), bv = val(b);
    if (av < bv) return -1 * facSort.dir;
    if (av > bv) return 1 * facSort.dir;
    // Stable tiebreak on name so equal keys keep a predictable order.
    return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
  });
}

function updateFacCarets() {
  if (!fel.table) return;
  fel.table.querySelectorAll('th[data-fac-sort]').forEach(th => {
    const base = th.textContent.replace(/[▲▼]\s*$/, '').trimEnd();
    th.textContent = base + (th.dataset.facSort === facSort.key ? (facSort.dir === 1 ? ' ▲' : ' ▼') : '');
  });
}

const FAC_STATUS_BADGE = { planned: 'off', active: 'ok', inactive: 'warn', decommissioned: 'warn' };

// PM schedule interval labels (mirror the design's enumerated ISO-8601 set).
const PM_INTERVAL_LABELS = { P1W: 'Weekly', P1M: 'Monthly', P3M: 'Quarterly', P6M: 'Semi-annual', P1Y: 'Annual' };
function intervalLabel(iso) {
  if (!iso) return '—';
  return PM_INTERVAL_LABELS[iso] ? `${PM_INTERVAL_LABELS[iso]} (${iso})` : iso;
}

// Client-side next-due hint mirroring the server's anchor-based rule:
// the smallest anchor + k×interval that is >= today (never shifts on completion).
function pmNextDue(intervalIso, anchorISO) {
  if (!intervalIso || !anchorISO) return null;
  const m = /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?$/.exec(String(intervalIso).trim());
  if (!m) return null;
  const y = Number(m[1]) || 0, mo = Number(m[2]) || 0, w = Number(m[3]) || 0, d = Number(m[4]) || 0;
  if (!(y || mo || w || d)) return null;
  const cur = new Date(anchorISO);
  if (isNaN(cur)) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  let guard = 0;
  while (cur < today && guard++ < 5000) {
    if (y) cur.setFullYear(cur.getFullYear() + y);
    if (mo) cur.setMonth(cur.getMonth() + mo);
    if (w) cur.setDate(cur.getDate() + w * 7);
    if (d) cur.setDate(cur.getDate() + d);
  }
  return cur.toISOString().slice(0, 10);
}

function renderFacilities() {
  updateFacCarets();
  if (!facilities.length) {
    fel.tbody.innerHTML = '<tr><td colspan="8" style="color:var(--sub);">No facilities.</td></tr>';
    renderFacPager(0, 0, 0);
    return;
  }
  const rows = sortedFacilities();
  const pageCount = Math.max(1, Math.ceil(rows.length / FAC_PAGE_SIZE));
  if (facPage > pageCount - 1) facPage = pageCount - 1;
  if (facPage < 0) facPage = 0;
  if (facSelectedId && !facilities.some(f => f.id === facSelectedId)) facSelectedId = null;
  const start = facPage * FAC_PAGE_SIZE;
  const pageRows = rows.slice(start, start + FAC_PAGE_SIZE);
  fel.tbody.innerHTML = pageRows.map(f => {
    const isOpen = facSelectedId === f.id;
    if (isOpen) fel._current = facDetailCache[f.id] || f;
    return facRowHtml(f, isOpen) + (isOpen ? facDetailRowHtml(f) : '');
  }).join('');
  renderFacPager(start, pageRows.length, rows.length);
  // The Leaflet container only exists once the open row is in the DOM.
  if (facSelectedId && facDetailCache[facSelectedId]) renderFacMap(facDetailCache[facSelectedId]);
}

function facRowHtml(f, isOpen) {
  return `<tr data-fac="${escapeHtml(f.id)}" class="inv-clickable-row${isOpen ? ' inv-row-open' : ''}" style="cursor:pointer;">
      <td>${escapeHtml(f.name)}</td>
      <td>${escapeHtml(f.code || '—')}</td>
      <td>${badge(titleCase(f.type), 'off')}</td>
      <td>${escapeHtml(f.service_area || '—')}</td>
      <td>${f.commissioned_at ? escapeHtml(f.commissioned_at.slice(0, 10)) : '—'}</td>
      <td style="text-align:right;">${f.asset_count ?? 0}</td>
      <td style="text-align:right;">${f.open_wo_count ?? 0}</td>
      <td>${badge(titleCase(f.status), FAC_STATUS_BADGE[f.status] || '')}</td>
    </tr>`;
}

function renderFacPager(start, shown, total) {
  if (fel.pageInfo) fel.pageInfo.textContent = total ? `${start + 1}–${start + shown} of ${total}` : '0 facilities';
  const pageCount = Math.max(1, Math.ceil(total / FAC_PAGE_SIZE));
  if (fel.prev) fel.prev.disabled = facPage <= 0;
  if (fel.next) fel.next.disabled = facPage >= pageCount - 1;
}

function setFacSort(key) {
  if (!key) return;
  if (facSort.key === key) facSort.dir *= -1;   // toggle direction on the same column
  else facSort = { key, dir: 1 };
  facPage = 0;
  renderFacilities();
}

// Toggle the row open inline (collapse it if it's already the open one).
async function toggleFacDetail(id) {
  if (facSelectedId === id) { closeFacDetail(); return; }
  if (facMap) { try { facMap.remove(); } catch { /* noop */ } facMap = null; }
  facSelectedId = id;
  renderFacilities();  // paints the "Loading…" row immediately
  try {
    // Facility + its PM schedules (middle column) in one round-trip.
    const [f, scheds] = await Promise.all([
      api.fetch(API.infraFacility(id), { method: 'GET' }),
      api.fetch(API.infraPmSchedules(`facility_id=${encodeURIComponent(id)}`), { method: 'GET' }).catch(() => []),
    ]);
    f._schedules = Array.isArray(scheds) ? scheds : [];
    facDetailCache[id] = f;
    if (facSelectedId === id) renderFacilities();
  } catch (e) {
    flash(e.message || 'Failed to load facility', 'error');
    if (facSelectedId === id) { facSelectedId = null; renderFacilities(); }
  }
}

function closeFacDetail() {
  if (facMap) { try { facMap.remove(); } catch { /* noop */ } facMap = null; }
  facSelectedId = null;
  fel._current = null;
  renderFacilities();
}

// The inline detail row: rendered from the cached fetch; "Loading…" until it resolves.
function facDetailRowHtml(f) {
  const d = facDetailCache[f.id];
  const full = d || f;
  const inner = d ? facDetailInner(d) : '<span style="color:var(--sub);">Loading…</span>';
  const meta = `${badge(titleCase(full.type), 'off')} ${badge(titleCase(full.status), FAC_STATUS_BADGE[full.status] || '')}`;
  return `<tr class="inv-inline-detail"><td colspan="${FAC_COLSPAN}"><div class="inv-inline-box">
      <div class="inv-inline-head">
        <div><strong>${escapeHtml(full.name)}</strong><div class="inv-detail-sub" style="font-size:12px; color:var(--sub);">${meta}</div></div>
        <div style="display:flex; align-items:flex-start; gap:8px; flex-shrink:0;">
          <div class="inv-inline-actions" style="margin-top:0; justify-content:flex-end;">
            <button class="btn" data-fac-edit>Edit</button>${facDeleteButton(d)}
          </div>
          <button type="button" class="btn ghost" data-fac-close title="Close">✕</button>
        </div>
      </div>
      ${inner}
    </div></td></tr>`;
}

// Deleting a site removes a register entry the whole module hangs off, so it is
// manager/root only and offered only once the server says it is safe. The button
// is disabled with the reason rather than hidden: a manager looking for it should
// find out *why* they cannot, not wonder where it went.
function facDeleteButton(d) {
  if (!hasRole('root', 'infra_manager')) return '';
  if (!d) return '';                               // detail still loading
  const blocking = (d.delete_blockers || []).filter((b) => !b.cascades);
  if (!blocking.length) return '<button class="btn danger" data-fac-delete>Delete</button>';
  const why = blocking.map((b) => `${b.count} ${b.kind.replace(/_/g, ' ')}`).join(', ');
  // Keeps `data-fac-delete` so the button is the same element either way — a
  // disabled one cannot fire a click, and the attribute is how it is identified.
  return `<button class="btn danger" data-fac-delete disabled title="${escapeHtml(`Still referenced by ${why}`)}">Delete</button>`;
}

async function deleteFacility() {
  const f = fel._current;
  if (!f) return;
  // Name what goes with it: documents cascade, and a confirmation that hides that
  // is not a confirmation.
  const cascading = (f.delete_blockers || []).filter((b) => b.cascades);
  const extra = cascading.length
    ? `\n\nThis also deletes ${cascading.map((b) => `${b.count} ${b.kind.replace(/_/g, ' ')}`).join(' and ')}.`
    : '';
  if (!window.confirm(`Delete "${f.name}"? This cannot be undone.${extra}`)) return;
  try {
    await api.fetch(API.infraFacilityDelete(f.id), { method: 'DELETE' });
    flash('Facility deleted', 'success');
    delete facDetailCache[f.id];
    closeFacDetail();
    loadFacilities();
  } catch (e) {
    // A 409 arrives when something started referencing it since the page loaded, so
    // re-open the detail against fresh data — the button will then explain itself.
    flash(e.message || 'Could not delete this facility', 'error');
    const id = facSelectedId;
    if (id) {
      delete facDetailCache[id];
      closeFacDetail();
      await toggleFacDetail(id);
    }
  }
}

// 3-column detail: [assets + open WOs] · [maintenance schedules] · [details → map → documents].
function facDetailInner(f) {
  const kv = [];
  if (f.code) kv.push(kvRow('Code', escapeHtml(f.code)));
  if (f.service_area) kv.push(kvRow('Service area', escapeHtml(f.service_area)));
  if (f.latitude != null && f.longitude != null) kv.push(kvRow('GPS', `${escapeHtml(f.latitude)}, ${escapeHtml(f.longitude)}`));
  kv.push(kvRow('Commission date', f.commissioned_at ? escapeHtml(f.commissioned_at.slice(0, 10)) : '—'));
  if (f.address) kv.push(kvRow('Address', escapeHtml(f.address)));

  const assets = f.assets || [];
  const assetsHtml = assets.length
    ? '<ul style="margin:0; padding:0; list-style:none;">' + assets.map(a => `<li style="display:flex; align-items:center; gap:6px; padding:2px 0;">
        <span style="flex:1; min-width:0;">${escapeHtml(a.name)} <span style="color:var(--sub);">${escapeHtml(a.serial)}</span> ${badge(titleCase(a.state), 'off')}</span>
        <button type="button" class="btn ghost" data-fac-unlink-asset="${escapeHtml(a.unit_id)}" title="Delink from this facility (does not delete the asset)" style="padding:0 6px; line-height:1.4; flex-shrink:0;">🔗✕</button>
      </li>`).join('') + '</ul>'
    : '<span style="color:var(--sub);">No linked assets.</span>';
  const wos = f.open_work_orders || [];
  const wosHtml = wos.length
    ? '<ul style="margin:0; padding-left:16px;">' + wos.map(w => `<li>${escapeHtml(w.subject)} ${badge(titleCase(w.status), 'off')}${w.due_at ? ` <span style="color:var(--sub);">${escapeHtml(fmtDateTime(w.due_at))}</span>` : ''}</li>`).join('') + '</ul>'
    : '<span style="color:var(--sub);">No open work orders.</span>';
  const showMap = f.latitude != null && f.longitude != null && typeof window.L !== 'undefined';
  const lbl = (txt, extra = '') => `<div class="label" style="font-size:12px; color:var(--sub); margin-bottom:6px; ${extra}">${txt}</div>`;

  return `<div class="inv-fac-grid">
    <div class="inv-fac-col">
      <div class="label" style="font-size:12px; color:var(--sub); margin-bottom:6px; display:flex; align-items:center; gap:8px;">Linked assets
        <button type="button" class="btn ghost" data-fac-add-assets style="padding:2px 8px;">＋ Add</button></div>
      <div style="font-size:13px;">${assetsHtml}</div>
      ${lbl('Open work orders', 'margin-top:14px;')}
      <div style="font-size:13px;">${wosHtml}</div>
    </div>
    <div class="inv-fac-col">
      ${lbl('Maintenance schedule')}
      <div style="font-size:13px;">${facSchedulesHtml(f._schedules)}</div>
    </div>
    <div class="inv-fac-col">
      ${lbl('Details')}
      <div class="inv-kv" style="grid-template-columns:1fr; margin:4px 0 0;">${kv.join('')}</div>
      <div id="opsFacDetailMap" style="height:180px; border-radius:8px; margin:12px 0; display:${showMap ? 'block' : 'none'};"></div>
      <div class="label" style="font-size:12px; color:var(--sub); margin-bottom:6px; display:flex; align-items:center; gap:8px;">Site documents
        <label class="btn ghost" style="cursor:pointer; padding:2px 8px;">＋ Upload<input type="file" data-fac-doc-file style="display:none;" /></label></div>
      <div data-fac-docs style="font-size:13px;">${facDocsHtml(f)}</div>
    </div>
  </div>`;
}

function facSchedulesHtml(scheds) {
  if (!scheds || !scheds.length) return '<span style="color:var(--sub);">No maintenance schedules.</span>';
  return '<ul style="margin:0; padding:0; list-style:none;">' + scheds.map(s => {
    const status = s.overdue_count > 0 ? badge(`Overdue ×${s.overdue_count}`, 'warn')
      : (!s.is_active ? badge('Paused', 'off') : badge('On track', 'ok'));
    const sub = [intervalLabel(s.maintenance_interval), s.next_due ? `next ${s.next_due}` : '', s.work_set_name || '']
      .filter(Boolean).map(escapeHtml).join(' · ');
    return `<li style="padding:5px 0; border-bottom:1px solid var(--border-soft);">
      <div style="display:flex; justify-content:space-between; gap:8px; align-items:baseline;"><strong>${escapeHtml(s.subject)}</strong>${status}</div>
      <div style="font-size:12px; color:var(--sub);">${sub}</div>
    </li>`;
  }).join('') + '</ul>';
}

function facDocsHtml(f) {
  const docs = f.documents || [];
  return docs.length
    ? '<ul style="margin:0; padding-left:16px;">' + docs.map(d => `<li><a href="#" data-doc-dl="${escapeHtml(d.id)}">${escapeHtml(d.original_filename)}</a>${d.note ? ` <span style="color:var(--sub);">${escapeHtml(d.note)}</span>` : ''}</li>`).join('') + '</ul>'
    : '<span style="color:var(--sub);">No documents.</span>';
}

let facMap = null;
function renderFacMap(f) {
  const mapEl = document.getElementById('opsFacDetailMap');
  if (!mapEl) return;
  if (f.latitude == null || f.longitude == null || typeof window.L === 'undefined') { mapEl.style.display = 'none'; return; }
  mapEl.style.display = 'block';
  try {
    if (facMap) { facMap.remove(); facMap = null; }
    facMap = window.L.map(mapEl).setView([f.latitude, f.longitude], 15);
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '© OpenStreetMap' }).addTo(facMap);
    window.L.marker([f.latitude, f.longitude]).addTo(facMap);
    setTimeout(() => { try { facMap && facMap.invalidateSize(); } catch { /* noop */ } }, 60);
  } catch { mapEl.style.display = 'none'; }
}

async function uploadFacDoc(file) {
  if (!file || !facSelectedId) return;
  const id = facSelectedId;
  const fd = new FormData();
  fd.append('file', file);
  try {
    await api.fetch(API.infraFacilityDocuments(id), { method: 'POST', body: fd });
    flash('Document uploaded', 'success');
    const f = await api.fetch(API.infraFacility(id), { method: 'GET' });
    f._schedules = facDetailCache[id]?._schedules || [];  // docs don't touch schedules
    facDetailCache[id] = f;
    if (facSelectedId === id) renderFacilities();
  } catch (e) { flash(e.message || 'Upload failed', 'error'); }
}

function openFacility(f) {
  fel.form.reset();
  fel.form.dataset.editId = f ? f.id : '';
  fel.dlgTitle.textContent = f ? 'Edit facility' : 'New facility';
  if (f) {
    fel.name.value = f.name; fel.code.value = f.code || ''; fel.type.value = f.type; fel.status.value = f.status;
    fel.lat.value = f.latitude ?? ''; fel.lng.value = f.longitude ?? ''; fel.serviceArea.value = f.service_area || '';
    fel.address.value = f.address || '';
    fel.commissionedAt.value = f.commissioned_at ? f.commissioned_at.slice(0, 10) : '';
  }
  openDialog(fel.dlg);
}

async function submitFacility(ev) {
  ev.preventDefault();
  const id = fel.form.dataset.editId;
  const body = {
    name: fel.name.value.trim(), code: fel.code.value.trim() || null, type: fel.type.value, status: fel.status.value,
    latitude: fel.lat.value === '' ? null : Number(fel.lat.value),
    longitude: fel.lng.value === '' ? null : Number(fel.lng.value),
    service_area: fel.serviceArea.value.trim() || null,
    commissioned_at: fel.commissionedAt.value || null,
    address: fel.address.value.trim() || null,
  };
  if (!body.name) { flash('Name is required', 'error'); return; }
  try {
    const saved = await api.fetch(id ? API.infraFacility(id) : API.infraFacilities(), { method: id ? 'PATCH' : 'POST', body: JSON.stringify(body) });
    closeDialog(fel.dlg);
    flash('Saved', 'success');
    facSelectedId = saved.id;
    saved._schedules = facDetailCache[saved.id]?._schedules || [];  // an edit doesn't change schedules
    facDetailCache[saved.id] = saved;
    await loadFacilities();  // refresh list + re-render the open row inline from cache
  } catch (e) { flash(e.message || 'Save failed', 'error'); }
}

function wireFacEvents() {
  fel.refreshBtn?.addEventListener('click', () => initFacilities());
  fel.newBtn?.addEventListener('click', () => openFacility(null));
  let t = null;
  fel.search?.addEventListener('input', () => { clearTimeout(t); t = setTimeout(() => loadFacilities(), 250); });
  [fel.typeFilter, fel.statusFilter].forEach(s => s?.addEventListener('change', () => loadFacilities()));
  fel.table?.querySelector('thead')?.addEventListener('click', (e) => {
    const th = e.target.closest('th[data-fac-sort]'); if (th) setFacSort(th.dataset.facSort);
  });
  fel.prev?.addEventListener('click', () => { facPage -= 1; renderFacilities(); });
  fel.next?.addEventListener('click', () => { facPage += 1; renderFacilities(); });
  // Row/detail actions are delegated on the tbody (the inline detail lives inside it).
  fel.tbody?.addEventListener('click', (e) => {
    if (e.target.closest('[data-fac-close]')) { closeFacDetail(); return; }
    if (e.target.closest('[data-fac-edit]')) { if (fel._current) openFacility(fel._current); return; }
    if (e.target.closest('[data-fac-delete]')) { void deleteFacility(); return; }
    const unlink = e.target.closest('[data-fac-unlink-asset]');
    if (unlink) { unlinkFacilityAsset(unlink.dataset.facUnlinkAsset); return; }
    if (e.target.closest('[data-fac-add-assets]')) { openFacAssetPicker(); return; }
    const dl = e.target.closest('[data-doc-dl]');
    if (dl) {
      if (facSelectedId) { e.preventDefault(); window.open(API.infraFacilityDocumentDownload(facSelectedId, dl.dataset.docDl), '_blank'); }
      return;
    }
    // A click anywhere else on a facility row toggles its inline detail.
    const tr = e.target.closest('tr[data-fac]');
    if (tr) toggleFacDetail(tr.dataset.fac);
  });
  fel.tbody?.addEventListener('change', (e) => {
    const inp = e.target.closest('[data-fac-doc-file]');
    if (inp) { const f = inp.files && inp.files[0]; if (f) uploadFacDoc(f); inp.value = ''; }
  });
  fel.form?.addEventListener('submit', submitFacility);
  fel.importBtn?.addEventListener('click', openFacImport);
  fel.importPreviewBtn?.addEventListener('click', () => runFacImport(false));
  fel.importCommitBtn?.addEventListener('click', () => runFacImport(true));
  fel.importFile?.addEventListener('change', () => { if (fel.importCommitBtn) fel.importCommitBtn.disabled = true; });
  // Mass-assign picker (static dialog elements — wired once).
  const g = (id) => document.getElementById(id);
  g('opsFacAssignSearch')?.addEventListener('input', renderAssignList);
  g('opsFacAssignLoc')?.addEventListener('change', renderAssignList);
  g('opsFacAssignAll')?.addEventListener('change', (e) => {
    const rows = filteredAssignAssets();
    rows.forEach(a => (e.target.checked ? assignSelected.add(a.unit_id) : assignSelected.delete(a.unit_id)));
    renderAssignList();
  });
  g('opsFacAssignList')?.addEventListener('change', (e) => {
    const cb = e.target.closest('[data-assign-cb]'); if (!cb) return;
    if (cb.checked) assignSelected.add(cb.value); else assignSelected.delete(cb.value);
    syncAssignFooter();
  });
  g('opsFacAssignForm')?.addEventListener('submit', submitAssignAssets);
  wireDismiss(['opsFacDlg', 'opsFacImportDlg', 'opsFacAssignDlg']);
}

// --- Mass-assign assets to a facility ------------------------------------- //

let assignAssets = [];            // the full unassigned pool for the open picker
let assignSelected = new Set();   // ticked unit_ids

async function openFacAssetPicker() {
  if (!facSelectedId) return;
  const dlg = document.getElementById('opsFacAssignDlg');
  if (!dlg) return;
  assignSelected = new Set();
  assignAssets = [];
  const f = facDetailCache[facSelectedId];
  const g = (id) => document.getElementById(id);
  g('opsFacAssignTitle').textContent = f ? `Assign assets to ${f.name}` : 'Assign assets';
  g('opsFacAssignSearch').value = '';
  g('opsFacAssignList').innerHTML = '<span style="color:var(--sub);">Loading…</span>';
  syncAssignFooter([]);
  openDialog(dlg);
  try {
    const data = await api.fetch(API.infraFacilityAssignableAssets(), { method: 'GET' });
    assignAssets = data.items || [];
    populateAssignLocations();
    renderAssignList();
  } catch (e) {
    g('opsFacAssignList').innerHTML = `<span style="color:var(--err);">${escapeHtml(e.message || 'Failed to load assets')}</span>`;
  }
}

function populateAssignLocations() {
  const sel = document.getElementById('opsFacAssignLoc');
  if (!sel) return;
  const seen = new Map();
  assignAssets.forEach(a => { if (a.location_id && !seen.has(a.location_id)) seen.set(a.location_id, a.location_name || a.location_id); });
  const opts = Array.from(seen.entries()).sort((x, y) => String(x[1]).localeCompare(String(y[1])))
    .map(([id, name]) => `<option value="${escapeHtml(id)}">${escapeHtml(name)}</option>`).join('');
  const noLoc = assignAssets.some(a => !a.location_id) ? '<option value="__none__">Already out of stores</option>' : '';
  sel.innerHTML = `<option value="">All stores</option>${opts}${noLoc}`;
  sel.value = '';
}

function filteredAssignAssets() {
  const q = (document.getElementById('opsFacAssignSearch').value || '').trim().toLowerCase();
  const loc = document.getElementById('opsFacAssignLoc').value;
  return assignAssets.filter(a => {
    if (loc === '__none__') { if (a.location_id) return false; }
    else if (loc) { if (a.location_id !== loc) return false; }
    if (q) {
      const hay = [a.serial, a.name, a.sku, a.location_name].map(x => (x || '').toLowerCase());
      if (!hay.some(h => h.includes(q))) return false;
    }
    return true;
  });
}

function renderAssignList() {
  const list = document.getElementById('opsFacAssignList');
  if (!list) return;
  const rows = filteredAssignAssets();
  list.innerHTML = rows.length
    ? rows.map(a => `<label style="display:flex; align-items:center; gap:8px; padding:3px 2px;">
        <input type="checkbox" data-assign-cb value="${escapeHtml(a.unit_id)}"${assignSelected.has(a.unit_id) ? ' checked' : ''}>
        <span style="flex:1; min-width:0;"><strong>${escapeHtml(a.serial)}</strong> · ${escapeHtml(a.name)}${a.sku ? ` <span style="color:var(--sub);">${escapeHtml(a.sku)}</span>` : ''}${a.is_fixed_asset ? ' ' + badge('Fixed', 'off') : ''}</span>
        <span style="color:var(--sub); font-size:12px; flex-shrink:0;">${escapeHtml(a.location_name || 'Out of stores')}</span>
      </label>`).join('')
    : `<span style="color:var(--sub);">No unassigned assets${assignAssets.length ? ' match your filters' : ''}.</span>`;
  syncAssignFooter(rows);
}

function syncAssignFooter(rows) {
  rows = rows || filteredAssignAssets();
  const allCb = document.getElementById('opsFacAssignAll');
  if (allCb) allCb.checked = rows.length > 0 && rows.every(a => assignSelected.has(a.unit_id));
  const submit = document.getElementById('opsFacAssignSubmit');
  if (submit) {
    submit.disabled = assignSelected.size === 0;
    submit.textContent = assignSelected.size ? `Assign ${assignSelected.size}` : 'Assign';
  }
  const count = document.getElementById('opsFacAssignCount');
  if (count) count.textContent = `${rows.length} shown · ${assignSelected.size} selected`;
}

async function submitAssignAssets(ev) {
  ev.preventDefault();
  if (!facSelectedId || !assignSelected.size) return;
  const id = facSelectedId;
  const submit = document.getElementById('opsFacAssignSubmit');
  if (submit) submit.disabled = true;
  try {
    const res = await api.fetch(API.infraFacilityAssets(id), {
      method: 'POST', body: JSON.stringify({ unit_ids: Array.from(assignSelected) }),
    });
    closeDialog(document.getElementById('opsFacAssignDlg'));
    flash(`Assigned ${res.assigned} asset${res.assigned === 1 ? '' : 's'}${res.skipped ? ` · ${res.skipped} skipped` : ''}`, 'success');
    if (res.facility) {
      res.facility._schedules = facDetailCache[id]?._schedules || [];  // assigning assets doesn't change schedules
      facDetailCache[id] = res.facility;
    }
    await loadFacilities();  // asset_count on the row changed; re-renders the open detail from cache
  } catch (e) {
    flash(e.message || 'Assign failed', 'error');
    if (submit) submit.disabled = false;
  }
}

// Delink one asset from the open facility (mistake fix — the asset is not deleted).
async function unlinkFacilityAsset(unitId) {
  const id = facSelectedId;
  if (!id || !unitId) return;
  if (!window.confirm('Delink this asset from the facility? It goes back into the store it came from.')) return;
  try {
    const detail = await api.fetch(API.infraFacilityAssetUnlink(id, unitId), { method: 'DELETE' });
    detail._schedules = facDetailCache[id]?._schedules || [];  // delinking doesn't change schedules
    facDetailCache[id] = detail;
    flash('Asset delinked and returned to its store', 'success');
    await loadFacilities();  // asset_count on the row changed; re-renders the open detail from cache
  } catch (e) {
    flash(e.message || 'Failed to delink asset', 'error');
  }
}

function openFacImport() {
  if (!fel.importDlg) return;
  if (fel.importFile) fel.importFile.value = '';
  if (fel.importResult) fel.importResult.innerHTML = '';
  if (fel.importCommitBtn) fel.importCommitBtn.disabled = true;
  openDialog(fel.importDlg);
}

async function runFacImport(commitIt) {
  const file = fel.importFile && fel.importFile.files && fel.importFile.files[0];
  if (!file) { flash('Choose a workbook first.', 'error'); return; }
  const fd = new FormData();
  fd.append('file', file);
  const btn = commitIt ? fel.importCommitBtn : fel.importPreviewBtn;
  if (btn) btn.disabled = true;
  try {
    const url = commitIt ? API.infraFacilitiesImportCommit() : API.infraFacilitiesImportPreview();
    const res = await api.fetch(url, { method: 'POST', body: fd });
    renderFacImportResult(res, commitIt);
    if (!commitIt) {
      if (fel.importCommitBtn) fel.importCommitBtn.disabled = false;  // preview done — allow applying
    } else {
      flash(`Imported ${res.created} facilit${res.created === 1 ? 'y' : 'ies'}.`, 'success');
      if (fel.importCommitBtn) fel.importCommitBtn.disabled = true;
      loadFacilities();  // reflect the new rows
    }
  } catch (err) {
    flash(err.message || 'Import failed', 'error');
  } finally {
    if (btn && !(commitIt && fel.importCommitBtn && fel.importCommitBtn.disabled)) btn.disabled = false;
  }
}

function renderFacImportResult(res, committed) {
  if (!fel.importResult) return;
  const errs = (res.errors || [])
    .map(e => `<div style="color:var(--err); font-size:12px;">Row ${e.row}: ${escapeHtml(e.message)}</div>`).join('');
  fel.importResult.innerHTML = `
    <div style="margin-bottom:6px; font-weight:600;">${committed ? '✅ Imported' : 'Preview — nothing saved yet'}</div>
    <div style="font-size:13px;">Created <strong>${res.created}</strong> · skipped <span style="color:var(--sub);">${res.skipped}</span> · errors <span style="${res.total_errors ? 'color:var(--err);' : ''}">${res.total_errors}</span></div>
    ${errs ? `<div style="margin-top:6px;">${errs}</div>` : ''}`;
}

// Shared: wire dialog dismiss buttons once.
function wireDismiss(ids) {
  document.querySelectorAll('[data-dialog-dismiss]').forEach(b => {
    if (b.dataset.opsWired || !ids.includes(b.dataset.dialogDismiss)) return;
    b.dataset.opsWired = '1';
    b.addEventListener('click', () => closeDialog(document.getElementById(b.dataset.dialogDismiss)));
  });
}

// ===========================================================================
// MAINTENANCE CALENDAR (AM-7)
// ===========================================================================

let calDomReady = false;
let cel = {};
let pmSchedules = [];        // [{id, subject, scope, work_set_id, ...}]
let pmSchedPage = 0;         // schedules tab: facility-group page index
const PM_SCHED_FAC_PER_PAGE = 8;  // facility groups shown per schedules page
const pmSchedExpanded = new Set();   // expanded facility-group labels (tree state)
let pmCoverageExpanded = false;      // coverage gap card expanded?
let pmEligibleAssets = [];   // covered-asset picker feed
let pmFacilities = [];       // facility <select> feed
let workGroups = [];
let pmTechnicians = [];      // for work-group team editing
let editingScheduleId = null;

async function ensureWorkSetsLoaded() {
  if (workSets.length) return;
  try { workSets = await api.fetch(API.infraWorkSets(), { method: 'GET' }) || []; } catch { workSets = []; }
}
async function ensureEligibleAssets() {
  if (pmEligibleAssets.length) return;
  try { pmEligibleAssets = await api.fetch(API.infraPmEligibleAssets(), { method: 'GET' }) || []; }
  catch { pmEligibleAssets = []; }
}
async function ensureFacilities() {
  if (pmFacilities.length) return;
  try { const d = await api.fetch(API.infraFacilities(), { method: 'GET' }); pmFacilities = (d && d.items) || d || []; }
  catch { pmFacilities = []; }
}
async function ensureTechnicians() {
  if (pmTechnicians.length) return;
  try { const d = await api.fetch(API.infraTechnicians(), { method: 'GET' }); pmTechnicians = (d && d.items) || d || []; }
  catch { pmTechnicians = []; }
}

function assetLabel(u) { return `${u.serial}${u.item_name ? ' · ' + u.item_name : ''}`; }

function resolveCalDom() {
  if (calDomReady) return;
  calDomReady = true;
  const g = (id) => document.getElementById(id);
  cel = {
    tabs: g('opsPmTabs'), upcomingPanel: g('opsPmUpcomingPanel'),
    schedulesPanel: g('opsPmSchedulesPanel'), groupsPanel: g('opsPmGroupsPanel'),
    horizon: g('opsPmHorizon'), refreshBtn: g('opsPmRefreshBtn'), container: g('opsPmCalendar'),
    actWrap: g('opsPmActivity'), actLegend: g('opsPmActLegend'),
    actOverall: g('opsPmActOverall'), actStaff: g('opsPmActStaff'),
    schedRefreshBtn: g('opsPmSchedRefreshBtn'), addSchedBtn: g('opsPmAddSchedBtn'),
    sweepBtn: g('opsPmSweepBtn'), schedTbody: g('opsPmSchedTbody'),
    schedSearch: g('opsPmSchedSearch'), schedPager: g('opsPmSchedPager'),
    coverage: g('opsPmCoverage'),
    template: g('opsPmTemplate'), newTplWrap: g('opsPmNewTplWrap'),
    newTplName: g('opsPmNewTplName'), newTplItems: g('opsPmNewTplItems'),
    wgRefreshBtn: g('opsWgRefreshBtn'), wgTbody: g('opsWgTbody'), wgDetail: g('opsWgDetail'),
    templatesPanel: g('opsPmTemplatesPanel'), tplTbody: g('opsTplTbody'), tplEditor: g('opsTplEditor'),
    tplRefreshBtn: g('opsTplRefreshBtn'), tplNewBtn: g('opsTplNewBtn'),
    dlg: g('opsPmDlg'), form: g('opsPmForm'), dlgTitle: g('opsPmDlgTitle'),
    subject: g('opsPmSubject'), facility: g('opsPmFacility'), workSet: g('opsPmWorkSet'),
    interval: g('opsPmInterval'), commissionedAt: g('opsPmCommissionedAt'),
    assetSearch: g('opsPmAssetSearch'), assetList: g('opsPmAssetList'),
    assetHint: g('opsPmAssetHint'), deleteBtn: g('opsPmDeleteBtn'),
  };
  cel.tabs?.addEventListener('click', (e) => {
    const b = e.target.closest('button[data-mtab]'); if (b) switchMaintTab(b.dataset.mtab);
  });
  cel.refreshBtn?.addEventListener('click', () => loadCalendar());
  cel.horizon?.addEventListener('change', () => loadCalendar());
  cel.tplRefreshBtn?.addEventListener('click', () => loadChecklistTemplates());
  cel.tplNewBtn?.addEventListener('click', () => openTemplateEditor(null));
  cel.tplTbody?.addEventListener('click', (e) => {
    const ed = e.target.closest('[data-tpl-edit]');
    if (ed) { openTemplateEditor(tplRows.find(t => t.id === ed.dataset.tplEdit)); return; }
    const del = e.target.closest('[data-tpl-del]');
    if (del) deleteTemplate(del.dataset.tplDel);
  });
  cel.tplEditor?.addEventListener('click', (e) => {
    if (e.target.closest('#opsTplSaveBtn')) { e.preventDefault(); saveTemplate(); }
    else if (e.target.closest('#opsTplCancelBtn')) {
      e.preventDefault(); tplEditing = null;
      cel.tplEditor.innerHTML = '<span style="color:var(--sub);">Select a template to edit, or create a new one.</span>';
    }
  });
  cel.schedRefreshBtn?.addEventListener('click', () => loadSchedules());
  cel.schedSearch?.addEventListener('input', () => { pmSchedPage = 0; renderSchedules(); });
  cel.schedPager?.addEventListener('click', (e) => {
    const b = e.target.closest('button[data-sched-page]');
    if (b) { pmSchedPage = Number(b.dataset.schedPage) || 0; renderSchedules(); }
  });
  // Tree toggle: clicking a facility group header expands/collapses its schedules.
  cel.schedTbody?.addEventListener('click', (e) => {
    const g = e.target.closest('[data-sched-group]');
    if (!g) return;
    const key = g.dataset.schedGroup;
    if (pmSchedExpanded.has(key)) pmSchedExpanded.delete(key); else pmSchedExpanded.add(key);
    renderSchedules();
  });
  // Coverage card: expand the gap list; quick-add a schedule for a facility.
  cel.coverage?.addEventListener('click', (e) => {
    const add = e.target.closest('[data-cov-add]');
    if (add) { openScheduleDialog(null, { facility_id: add.dataset.covAdd }); return; }
    if (e.target.closest('[data-cov-toggle]')) { pmCoverageExpanded = !pmCoverageExpanded; renderCoverage(); }
  });
  // Reveal the inline "define a new template" block when chosen.
  cel.template?.addEventListener('change', () => {
    if (cel.newTplWrap) cel.newTplWrap.hidden = cel.template.value !== '__new__';
  });
  cel.addSchedBtn?.addEventListener('click', () => openScheduleDialog(null));
  cel.sweepBtn?.addEventListener('click', runSweep);
  if (cel.sweepBtn && !hasRole('root', 'infra_manager')) cel.sweepBtn.hidden = true;
  cel.form?.addEventListener('submit', submitSchedule);
  cel.deleteBtn?.addEventListener('click', deleteSchedule);
  cel.assetSearch?.addEventListener('input', filterAssetCheckboxes);
  // Changing the facility rescopes which assets are selectable + defaults the anchor.
  cel.facility?.addEventListener('change', () => {
    const checked = Array.from(cel.assetList.querySelectorAll('input[data-asset-cb]:checked')).map(c => c.value);
    renderAssetCheckboxes(checked);
    autofillCommission();
  });
  // Ticking an asset (for a facility-less schedule) can seed the anchor too.
  cel.assetList?.addEventListener('change', (e) => {
    if (e.target.matches('input[data-asset-cb]')) autofillCommission();
  });
  cel.schedTbody?.addEventListener('click', (e) => {
    const b = e.target.closest('button[data-sched-edit]');
    if (b) { const s = pmSchedules.find(x => x.id === b.dataset.schedEdit); if (s) openScheduleDialog(s); }
  });
  cel.container?.addEventListener('click', (e) => {
    const row = e.target.closest('[data-sched-id]');
    if (row && row.dataset.schedId) {
      const s = pmSchedules.find(x => x.id === row.dataset.schedId);
      if (s) openScheduleDialog(s);
    }
  });
  cel.wgRefreshBtn?.addEventListener('click', () => loadWorkGroups());
  cel.wgTbody?.addEventListener('click', (e) => {
    const b = e.target.closest('button[data-wg-open]'); if (b) openWorkGroup(b.dataset.wgOpen);
  });
  wireDismiss(['opsPmDlg']);
}

function switchMaintTab(tab) {
  const t = tab || 'upcoming';
  cel.tabs.querySelectorAll('button[data-mtab]').forEach(b => b.classList.toggle('active', b.dataset.mtab === t));
  cel.upcomingPanel.hidden = t !== 'upcoming';
  cel.schedulesPanel.hidden = t !== 'schedules';
  cel.groupsPanel.hidden = t !== 'groups';
  if (cel.templatesPanel) cel.templatesPanel.hidden = t !== 'templates';
  if (t === 'upcoming') loadCalendar();
  else if (t === 'schedules') loadSchedules();
  else if (t === 'templates') loadChecklistTemplates();
  else loadWorkGroups();
}

// --- Checklist templates (reusable task lists applied to a work order) ------ //

let tplRows = [];
let tplEditing = null;   // the template being edited, or {} for a new one

async function loadChecklistTemplates() {
  if (!cel.tplTbody) return;
  try {
    tplRows = await api.fetch(API.infraChecklistTemplates('active_only=false'), { method: 'GET' }) || [];
  } catch (e) {
    tplRows = [];
    cel.tplTbody.innerHTML = '<tr><td colspan="4" style="color:var(--sub);">Failed to load templates.</td></tr>';
    return;
  }
  renderTemplateList();
}

function renderTemplateList() {
  if (!tplRows.length) {
    cel.tplTbody.innerHTML = '<tr><td colspan="4" style="color:var(--sub);">No templates yet — create one to standardise a job’s checklist.</td></tr>';
    return;
  }
  cel.tplTbody.innerHTML = tplRows.map(t => `<tr>
      <td><strong>${escapeHtml(t.name)}</strong><div style="color:var(--sub); font-size:11.5px;">${escapeHtml(t.code)}</div></td>
      <td>${t.work_order_type ? escapeHtml(titleCase(String(t.work_order_type).replace(/_/g, ' '))) : '<span style="color:var(--sub);">Any type</span>'}</td>
      <td>${(t.items || []).length}${t.is_active ? '' : ' ' + badge('inactive', 'warn')}</td>
      <td style="text-align:right; white-space:nowrap;">
        <button class="btn sm" data-tpl-edit="${t.id}">Edit</button>
        <button class="btn sm ghost" data-tpl-del="${t.id}">Delete</button>
      </td></tr>`).join('');
}

function openTemplateEditor(tpl) {
  tplEditing = tpl || { code: '', name: '', items: [], work_order_type: '', is_active: true };
  const isNew = !tplEditing.id;
  const types = ['', 'build_out', 'corrective_maint', 'preventive_maint', 'site_visit', 'inspection'];
  const opts = types.map(v => `<option value="${v}"${String(tplEditing.work_order_type || '') === v ? ' selected' : ''}>${v ? escapeHtml(titleCase(v.replace(/_/g, ' '))) : 'Any type'}</option>`).join('');
  cel.tplEditor.innerHTML = `
    <div class="ops-tpl-editor">
      <div style="font-weight:650; margin-bottom:8px;">${isNew ? 'New template' : 'Edit template'}</div>
      <label class="field"><span>Code *</span><input id="opsTplCode" type="text" value="${escapeHtml(tplEditing.code || '')}"${isNew ? '' : ' disabled'} placeholder="e.g. tower-buildout"></label>
      <label class="field"><span>Name *</span><input id="opsTplName" type="text" value="${escapeHtml(tplEditing.name || '')}" placeholder="e.g. Tower build-out"></label>
      <label class="field"><span>Applies to</span><select id="opsTplType">${opts}</select></label>
      <label class="field"><span>Tasks — one per line *</span><textarea id="opsTplItems" rows="8" placeholder="Confirm foundation &amp; anchor bolts&#10;Erect &amp; plumb mast&#10;Grounding kit + earth test">${escapeHtml((tplEditing.items || []).join('\n'))}</textarea></label>
      <label class="field" style="flex-direction:row; align-items:center; gap:8px;"><input id="opsTplActive" type="checkbox"${tplEditing.is_active === false ? '' : ' checked'}><span>Active</span></label>
      <div style="display:flex; gap:8px; margin-top:8px;">
        <button class="btn primary" id="opsTplSaveBtn">${isNew ? 'Create' : 'Save'}</button>
        <button class="btn ghost" id="opsTplCancelBtn">Cancel</button>
      </div>
    </div>`;
}

async function saveTemplate() {
  const code = (document.getElementById('opsTplCode')?.value || '').trim();
  const name = (document.getElementById('opsTplName')?.value || '').trim();
  const type = document.getElementById('opsTplType')?.value || '';
  const isActive = !!document.getElementById('opsTplActive')?.checked;
  const items = (document.getElementById('opsTplItems')?.value || '')
    .split('\n').map(s => s.trim()).filter(Boolean);
  if (!name || !items.length) { flash('A name and at least one task are required.', 'error'); return; }
  try {
    if (tplEditing && tplEditing.id) {
      await api.fetch(API.infraChecklistTemplate(tplEditing.id), {
        method: 'PATCH',
        body: JSON.stringify({ name, items, work_order_type: type || null, is_active: isActive }),
      });
      flash('Template saved.');
    } else {
      if (!code) { flash('A code is required.', 'error'); return; }
      await api.fetch(API.infraChecklistTemplates(), {
        method: 'POST',
        body: JSON.stringify({ code, name, items, work_order_type: type || null }),
      });
      flash('Template created.');
    }
    checklistTemplates = null;          // the WO-detail picker re-fetches
    tplEditing = null;
    cel.tplEditor.innerHTML = '<span style="color:var(--sub);">Select a template to edit, or create a new one.</span>';
    await loadChecklistTemplates();
  } catch (e) {
    flash(api.formatErrorDetail(e && e.body && e.body.detail) || e.message || 'Save failed', 'error');
  }
}

async function deleteTemplate(id) {
  if (!window.confirm('Delete this checklist template? Work orders already using it keep their tasks.')) return;
  try {
    await api.fetch(API.infraChecklistTemplate(id), { method: 'DELETE' });
    checklistTemplates = null;
    flash('Template deleted.');
    await loadChecklistTemplates();
  } catch (e) {
    flash(api.formatErrorDetail(e && e.body && e.body.detail) || e.message || 'Delete failed', 'error');
  }
}

async function initCalendar() {
  resolveCalDom();
  try { await loadCalendar(); } catch (e) { flash(e.message || 'Failed to load calendar', 'error'); }
}

// --- Upcoming calendar ---------------------------------------------------- //

async function loadCalendar() {
  const days = cel.horizon.value || '90';
  const data = await api.fetch(API.infraPmCalendar(`days=${days}`), { method: 'GET' });
  renderCalendar(data.items || []);
  loadMaintActivity();  // fire-and-forget; the chart is independent of the list
}

// --- Maintenance activity chart (mirrors the Tech-Support field-tech chart) --- //

async function loadMaintActivity() {
  if (!cel.actWrap) return;
  try {
    const d = await api.fetch(API.infraPmActivity('weeks=13'), { method: 'GET' });
    renderMaintActivity(d);
  } catch { cel.actWrap.hidden = true; }
}

// A two-line spark: amber = due backlog, green = completed. Shared y-scale.
function _mactSpark(a, b, gmax, { wide = false } = {}) {
  const W = wide ? 420 : 200, H = wide ? 56 : 40, P = 3, n = Math.max(a.length, b.length);
  if (!n || gmax <= 0) return '';
  const x = (i) => P + (i * (W - 2 * P)) / Math.max(1, n - 1);
  const y = (v) => H - P - (Math.max(0, v) / gmax) * (H - 2 * P);
  const line = (arr) => arr.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
  const area = (arr) => `${line(arr)} L${x(n - 1).toFixed(1)} ${(H - P).toFixed(1)} L${x(0).toFixed(1)} ${(H - P).toFixed(1)} Z`;
  const last = (arr) => arr.length ? arr[arr.length - 1] : 0;
  return `<svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" class="ops-mact-svg" aria-hidden="true">
    ${b && b.length ? `<path d="${area(b)}" class="ops-mact-area-done"/>` : ''}
    ${a && a.length ? `<path d="${line(a)}" class="ops-mact-line-due" fill="none" vector-effect="non-scaling-stroke"/>` : ''}
    ${b && b.length ? `<path d="${line(b)}" class="ops-mact-line-done" fill="none" vector-effect="non-scaling-stroke"/>` : ''}
    ${a && a.length ? `<circle cx="${x(n - 1).toFixed(1)}" cy="${y(last(a)).toFixed(1)}" r="2.4" class="ops-mact-dot-due"/>` : ''}
    ${b && b.length ? `<circle cx="${x(n - 1).toFixed(1)}" cy="${y(last(b)).toFixed(1)}" r="2.6" class="ops-mact-dot-done"/>` : ''}
  </svg>`;
}

function renderMaintActivity(d) {
  if (!cel.actWrap) return;
  const staff = Array.isArray(d && d.staff) ? d.staff : [];
  const due = (d && d.series && d.series.due) || [];
  const done = (d && d.series && d.series.completed) || [];
  const totals = (d && d.totals) || {};
  // Nothing to show if there's no PM activity at all.
  if (!due.some(Boolean) && !done.some(Boolean) && !staff.length) { cel.actWrap.hidden = true; return; }
  cel.actWrap.hidden = false;

  cel.actLegend.innerHTML = `
    <span class="ops-mact-lg"><span class="ops-mact-sw due"></span>Due (backlog)</span>
    <span class="ops-mact-lg"><span class="ops-mact-sw done"></span>Completed</span>
    <span class="ops-mact-chip">${Number(totals.due_now || 0)} due now · ${Number(totals.completed || 0)} completed</span>`;

  const gmax = Math.max(1, ...due, ...done) * 1.08;
  cel.actOverall.innerHTML = `<div class="ops-mact-spark wide">${_mactSpark(due, done, gmax, { wide: true })}</div>`;

  if (!staff.length) {
    cel.actStaff.innerHTML = '<div class="ops-mact-empty">No completions recorded in the last 13 weeks.</div>';
    return;
  }
  const smax = Math.max(1, ...staff.flatMap(s => s.weekly_completed || [])) * 1.08;
  cel.actStaff.innerHTML = staff.map(s => `
    <div class="ops-mact-cell">
      <div class="ops-mact-name" title="${escapeHtml(s.name || '')}">${escapeHtml(s.name || '')}</div>
      <div class="ops-mact-sub">${Number(s.total_completed || 0)} completed</div>
      <div class="ops-mact-spark">${_mactSpark([], s.weekly_completed || [], smax)}</div>
    </div>`).join('');
}

function calRowHtml(e, dateCell) {
  return `<tr data-sched-id="${escapeHtml(e.schedule_id)}" style="cursor:pointer;">
    <td>${dateCell}</td>
    <td>${escapeHtml(e.subject)}${e.facility_name ? ` <span style="color:var(--sub);">· ${escapeHtml(e.facility_name)}</span>` : ''}</td>
    <td>${badge(titleCase(e.scope), e.scope === 'facility' ? 'info' : 'off')}</td>
    <td>${escapeHtml(e.work_set_name || '—')}</td>
    <td style="text-align:center;">${e.asset_count || 0}</td>
    <td>${e.interval ? badge(e.interval, 'off') : '—'}</td>
  </tr>`;
}

function calTable(bodyRows) {
  return `<div class="table-wrap"><table>
    <thead><tr><th>Date</th><th>Subject</th><th>Scope</th><th>Work set</th><th>Assets</th><th>Interval</th></tr></thead>
    <tbody>${bodyRows}</tbody></table></div>`;
}

function renderCalendar(items) {
  if (!items.length) {
    cel.container.innerHTML = '<div style="color:var(--sub);">No maintenance due or upcoming in this window. Add a schedule under the <strong>Schedules</strong> tab.</div>';
    return;
  }
  // Backend returns overdue-first then upcoming; split for section rendering.
  const overdue = items.filter(e => e.overdue);
  const upcoming = items.filter(e => !e.overdue);
  const parts = [];

  if (overdue.length) {
    const rows = overdue.map(e => {
      const dc = `<span style="color:var(--danger,#c0392b); font-weight:600;">${escapeHtml(new Date(e.date).toLocaleDateString())}</span>`
        + `<div style="font-size:11px; color:var(--danger,#c0392b);">${e.days_overdue} day${e.days_overdue === 1 ? '' : 's'} overdue</div>`;
      return calRowHtml(e, dc);
    }).join('');
    parts.push(`<div style="margin-bottom:16px;">
      <div style="font-weight:700; color:var(--danger,#c0392b); margin-bottom:4px;">⚠ Overdue · ${overdue.length}</div>
      ${calTable(rows)}</div>`);
  }

  const groups = {};
  upcoming.forEach(e => {
    const key = new Date(e.date).toLocaleString(undefined, { year: 'numeric', month: 'long' });
    (groups[key] = groups[key] || []).push(e);
  });
  parts.push(...Object.entries(groups).map(([month, es]) => `
    <div style="margin-bottom:14px;">
      <div style="font-weight:600; margin-bottom:4px;">${escapeHtml(month)}</div>
      ${calTable(es.map(e => calRowHtml(e, escapeHtml(new Date(e.date).toLocaleDateString()))).join(''))}
    </div>`));

  cel.container.innerHTML = parts.join('');
}

// --- Schedules ------------------------------------------------------------ //

async function loadSchedules() {
  await Promise.all([ensureWorkSetsLoaded(), ensureFacilities()]);
  try {
    pmSchedules = await api.fetch(API.infraPmSchedules(), { method: 'GET' }) || [];
  } catch (e) { flash(e.message || 'Failed to load schedules', 'error'); return; }
  renderCoverage();
  renderSchedules();
}

function schedStatusCell(r) {
  if (!r.commissioned_at) return '<span style="color:var(--warn,#b8860b);">Needs commission date</span>';
  if (r.overdue_count > 0) return badge(`Overdue ×${r.overdue_count}`, 'warn');
  if (!r.is_active) return badge('Paused', 'off');
  return badge('On track', 'ok');
}

// A schedule row (no facility cell — the facility is the group header).
function schedRowHtml(r) {
  return `<tr>
    <td><strong>${escapeHtml(r.subject)}</strong>${r.checklist_template_name
        ? ` <span class="ops-tpl-chip" title="Checklist applied to each WO">📋 ${escapeHtml(r.checklist_template_name)}</span>` : ''}</td>
    <td>${badge(titleCase(r.scope), r.scope === 'facility' ? 'info' : 'off')}</td>
    <td>${escapeHtml(r.work_set_name || '—')}</td>
    <td>${r.maintenance_interval ? escapeHtml(intervalLabel(r.maintenance_interval)) : '—'}</td>
    <td style="text-align:center;">${(r.assets || []).length}</td>
    <td>${r.next_due ? escapeHtml(r.next_due) : '—'}</td>
    <td>${schedStatusCell(r)}</td>
    <td><button class="btn ghost" data-sched-edit="${escapeHtml(r.id)}">Edit</button></td>
  </tr>`;
}

// Grouped-by-facility, searchable, paginated schedules list. Search matches the
// subject / facility / work set / interval; groups are paged (facilities with
// overdue backlog float to the top); each group sub-lists its schedules.
function renderSchedules() {
  const term = (cel.schedSearch?.value || '').trim().toLowerCase();
  const match = (r) => !term || [r.subject, r.facility_name, r.work_set_name, r.maintenance_interval]
    .some(v => String(v || '').toLowerCase().includes(term));

  const groups = new Map();  // facility label -> rows
  for (const r of (pmSchedules || [])) {
    if (!match(r)) continue;
    const label = r.facility_name || (r.scope === 'facility' ? '(Unnamed facility)' : 'Asset-level (no facility)');
    (groups.get(label) || groups.set(label, []).get(label)).push(r);
  }
  if (!groups.size) {
    cel.schedTbody.innerHTML = `<tr><td colspan="8" style="color:var(--sub);">${term ? 'No schedules match your search.' : 'No schedules yet. Use “＋ Schedule” to add one.'}</td></tr>`;
    if (cel.schedPager) cel.schedPager.innerHTML = '';
    return;
  }

  // Order rows within a group, then order groups (overdue-heavy first, then name).
  const entries = [...groups.entries()].map(([label, rows]) => {
    rows.sort((a, b) => (b.overdue_count || 0) - (a.overdue_count || 0) || String(a.subject).localeCompare(String(b.subject)));
    const overdue = rows.reduce((n, r) => n + (r.overdue_count || 0), 0);
    return { label, rows, overdue };
  }).sort((a, b) => (b.overdue > 0) - (a.overdue > 0) || a.label.localeCompare(b.label));

  const pages = Math.max(1, Math.ceil(entries.length / PM_SCHED_FAC_PER_PAGE));
  if (pmSchedPage >= pages) pmSchedPage = pages - 1;
  const pageGroups = entries.slice(pmSchedPage * PM_SCHED_FAC_PER_PAGE, (pmSchedPage + 1) * PM_SCHED_FAC_PER_PAGE);

  // A tree: each facility is a collapsible node (expanded on demand, or when a
  // search term is active so matches are always visible).
  cel.schedTbody.innerHTML = pageGroups.map(g => {
    const open = term ? true : pmSchedExpanded.has(g.label);
    const chevron = open ? '▾' : '▸';
    const header = `<tr class="ops-group-row" data-sched-group="${escapeHtml(g.label)}" style="cursor:pointer;">
      <td colspan="8" style="background:var(--panel-2,#f4f6f8); font-weight:600;">
      <span style="display:inline-block; width:1em; color:var(--sub);">${chevron}</span>🏢 ${escapeHtml(g.label)}
      <span style="color:var(--sub); font-weight:400;">· ${g.rows.length} schedule${g.rows.length === 1 ? '' : 's'}</span>
      ${g.overdue > 0 ? ` ${badge(`Overdue ×${g.overdue}`, 'warn')}` : ''}</td></tr>`;
    return header + (open ? g.rows.map(schedRowHtml).join('') : '');
  }).join('');

  renderSchedPager(entries.length, pages);
}

// Coverage gap: facilities with no PM schedule at all. Small expandable card.
function renderCoverage() {
  if (!cel.coverage) return;
  const facilities = pmFacilities || [];
  if (!facilities.length) { cel.coverage.hidden = true; return; }
  const covered = new Set((pmSchedules || []).map(s => String(s.facility_id || '')).filter(Boolean));
  const gaps = facilities.filter(f => !covered.has(String(f.id)));
  cel.coverage.hidden = false;
  const pct = Math.round(((facilities.length - gaps.length) / facilities.length) * 100);
  if (!gaps.length) {
    cel.coverage.innerHTML = `<div class="ops-cov-head ok">✓ All ${facilities.length} facilities have a PM schedule.</div>`;
    return;
  }
  const head = `<div class="ops-cov-head" data-cov-toggle style="cursor:pointer;">
      <span style="color:var(--warn,#b8860b); font-weight:600;">⚠ ${gaps.length} of ${facilities.length} facilities have no PM schedule</span>
      <span style="color:var(--sub);"> · ${pct}% covered</span>
      <span style="color:var(--sub); float:right;">${pmCoverageExpanded ? '▾ hide' : '▸ show'}</span>
    </div>`;
  const list = pmCoverageExpanded ? `<div class="ops-cov-list">${gaps.map(f =>
      `<div class="ops-cov-row"><span>🏢 ${escapeHtml(f.name)}${f.type ? ` <span style="color:var(--sub);">· ${escapeHtml(f.type)}</span>` : ''}</span>
       <button class="btn ghost sm" data-cov-add="${escapeHtml(f.id)}">＋ Schedule</button></div>`).join('')}</div>` : '';
  cel.coverage.innerHTML = head + list;
}

function renderSchedPager(totalGroups, pages) {
  if (!cel.schedPager) return;
  if (pages <= 1) {
    cel.schedPager.innerHTML = `<span style="color:var(--sub); font-size:12px;">${totalGroups} facilit${totalGroups === 1 ? 'y' : 'ies'}</span>`;
    return;
  }
  const btn = (p, label, disabled) => `<button class="btn ghost" data-sched-page="${p}"${disabled ? ' disabled' : ''} style="padding:2px 10px;">${label}</button>`;
  cel.schedPager.innerHTML = `<div style="display:flex; align-items:center; gap:8px; font-size:12px;">
    ${btn(pmSchedPage - 1, '‹ Prev', pmSchedPage <= 0)}
    <span style="color:var(--sub);">Page ${pmSchedPage + 1} of ${pages} · ${totalGroups} facilities</span>
    ${btn(pmSchedPage + 1, 'Next ›', pmSchedPage >= pages - 1)}
  </div>`;
}

// --- Schedule dialog ------------------------------------------------------ //

// Default the PM anchor (commission date) from the chosen facility, else from
// the first ticked asset — without clobbering a value the user already set.
function autofillCommission() {
  if (cel.commissionedAt.value) return;
  const facId = cel.facility.value || '';
  let iso = '';
  if (facId) {
    const f = pmFacilities.find(x => String(x.id) === facId);
    iso = (f && f.commissioned_at) || '';
  } else {
    const firstChecked = cel.assetList.querySelector('input[data-asset-cb]:checked');
    if (firstChecked) {
      const u = pmEligibleAssets.find(x => String(x.unit_id) === firstChecked.value);
      iso = (u && u.commissioned_at) || '';
    }
  }
  if (iso) cel.commissionedAt.value = iso.slice(0, 10);
}

// The assets selectable for the current facility choice: a facility's own
// assets, else (no facility) the assets not attached to any facility.
function scopedAssets() {
  const facId = cel.facility.value || '';
  return facId
    ? pmEligibleAssets.filter(u => String(u.facility_id || '') === facId)
    : pmEligibleAssets.filter(u => !u.facility_id);
}

function renderAssetCheckboxes(selectedIds) {
  const sel = new Set((selectedIds || []).map(String));
  const facId = cel.facility.value || '';
  if (cel.assetHint) {
    cel.assetHint.textContent = facId
      ? 'Tick specific assets, or leave all unticked to cover every asset at this facility.'
      : 'Only assets not attached to any facility are shown. Pick a facility above to schedule its assets.';
  }
  const list = scopedAssets();
  if (!list.length) {
    cel.assetList.innerHTML = `<span style="color:var(--sub);">${facId ? 'No PM-eligible assets at this facility.' : 'No unassigned PM-eligible assets.'}</span>`;
    return;
  }
  cel.assetList.innerHTML = list.map(u =>
    `<label style="display:block; padding:2px 0;"><input type="checkbox" data-asset-cb value="${escapeHtml(u.unit_id)}"${sel.has(String(u.unit_id)) ? ' checked' : ''}> ${escapeHtml(assetLabel(u))}</label>`
  ).join('');
}

function filterAssetCheckboxes() {
  const q = (cel.assetSearch.value || '').toLowerCase();
  cel.assetList.querySelectorAll('label').forEach(l => {
    l.style.display = l.textContent.toLowerCase().includes(q) ? '' : 'none';
  });
}

async function openScheduleDialog(sched, prefill = {}) {
  resolveCalDom();
  await Promise.all([ensureWorkSetsLoaded(), ensureEligibleAssets(), ensureFacilities(), ensureChecklistTemplates()]);
  cel.form.reset();
  fillSelect(cel.workSet, workSets, { placeholder: '—', labelFn: w => w.name });
  fillSelect(cel.facility, pmFacilities, { placeholder: '— none —', labelFn: f => f.name });
  // Checklist-template picker: existing (active) templates + a "define new" option.
  if (cel.template) {
    const opts = (checklistTemplates || []).filter(t => t.is_active !== false)
      .map(t => `<option value="${escapeHtml(t.id)}">${escapeHtml(t.name)}</option>`).join('');
    cel.template.innerHTML = `<option value="">— none —</option>${opts}<option value="__new__">＋ Define a new template…</option>`;
  }
  if (cel.newTplWrap) { cel.newTplWrap.hidden = true; }
  if (cel.newTplName) cel.newTplName.value = '';
  if (cel.newTplItems) cel.newTplItems.value = '';

  editingScheduleId = sched ? sched.id : null;
  cel.dlgTitle.textContent = sched ? 'Edit PM schedule' : 'New PM schedule';
  cel.deleteBtn.style.display = sched ? '' : 'none';
  cel.assetSearch.value = '';

  cel.subject.value = sched ? sched.subject : '';
  cel.workSet.value = sched ? (sched.work_set_id || '') : '';
  cel.facility.value = sched ? (sched.facility_id || '') : (prefill.facility_id || '');
  // A schedule's facility defines its scope: `_validate_assets` server-side only
  // allows a facility schedule to cover assets *at* that facility, so moving one
  // between sites would invalidate its coverage. The update endpoint therefore does
  // not accept `facility_id` at all — so the field is hidden when editing rather
  // than left there offering a change that cannot happen. The schedules list is
  // grouped by facility, so the site is still obvious from where you opened this.
  const facilityRow = cel.facility.closest('label');
  if (facilityRow) facilityRow.hidden = Boolean(sched);
  cel.interval.value = sched ? (sched.maintenance_interval || '') : '';
  cel.commissionedAt.value = sched && sched.commissioned_at ? sched.commissioned_at.slice(0, 10) : '';
  if (cel.template) cel.template.value = sched ? (sched.checklist_template_id || '') : '';
  renderAssetCheckboxes(sched ? (sched.assets || []).map(a => a.unit_id) : []);
  openDialog(cel.dlg);
}

async function submitSchedule(ev) {
  ev.preventDefault();
  const subject = (cel.subject.value || '').trim();
  const asset_ids = Array.from(cel.assetList.querySelectorAll('input[data-asset-cb]:checked')).map(c => c.value);
  if (!subject) { flash('A subject is required', 'error'); return; }
  if (!cel.workSet.value) { flash('Pick a work set', 'error'); return; }
  if (!cel.interval.value) { flash('Pick an interval', 'error'); return; }
  // Resolve the checklist template: an existing one, none, or define a new one now.
  let checklist_template_id = cel.template ? (cel.template.value || null) : null;
  // Remembered so a failed save can undo it. The template has to exist *before* the
  // schedule that references it, so a rejected save would otherwise leave an
  // orphan behind — and every retry would add another.
  let createdTemplateId = null;
  try {
    if (checklist_template_id === '__new__') {
      const name = (cel.newTplName.value || '').trim();
      const items = (cel.newTplItems.value || '').split('\n').map(s => s.trim()).filter(Boolean);
      if (!name) { flash('Name the new checklist template', 'error'); return; }
      if (!items.length) { flash('Add at least one checklist task', 'error'); return; }
      const code = (name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'tpl')
        + '-' + Math.random().toString(36).slice(2, 6);
      const created = await api.fetch(API.infraChecklistTemplates(), {
        method: 'POST', body: JSON.stringify({ code, name, items }),
      });
      checklist_template_id = created.id;
      createdTemplateId = created.id;
      checklistTemplates = null;  // invalidate cache so the new one shows next open
    }
  } catch (e) { flash(e.message || 'Could not create the checklist template', 'error'); return; }

  const body = {
    subject,
    work_set_id: cel.workSet.value,
    maintenance_interval: cel.interval.value,
    commissioned_at: cel.commissionedAt.value || null,
    asset_ids,
    checklist_template_id,
  };
  // Create only. `PmScheduleUpdateRequest` forbids extra fields and does not declare
  // `facility_id`, so sending it on a PATCH failed every edit with a 422.
  if (!editingScheduleId) body.facility_id = cel.facility.value || null;
  try {
    if (editingScheduleId) {
      await api.fetch(API.infraPmSchedule(editingScheduleId), { method: 'PATCH', body: JSON.stringify(body) });
    } else {
      await api.fetch(API.infraPmSchedules(), { method: 'POST', body: JSON.stringify(body) });
    }
    closeDialog(cel.dlg);
    flash('Schedule saved', 'success');
    await loadSchedules();
  } catch (e) {
    flash(e.message || 'Save failed', 'error');
    // Undo the template this attempt created, so a failed save leaves the library
    // as it found it. Best-effort: if the cleanup itself fails the save error is
    // still the one worth showing.
    if (createdTemplateId) {
      try {
        await api.fetch(API.infraChecklistTemplate(createdTemplateId), { method: 'DELETE' });
        checklistTemplates = null;
      } catch { /* leave it; the save error is the one that matters */ }
    }
  }
}

async function deleteSchedule() {
  if (!editingScheduleId) return;
  if (!confirm('Delete this PM schedule?')) return;
  try {
    await api.fetch(API.infraPmSchedule(editingScheduleId), { method: 'DELETE' });
    closeDialog(cel.dlg);
    flash('Schedule deleted', 'success');
    await loadSchedules();
  } catch (e) { flash(e.message || 'Delete failed', 'error'); }
}

async function runSweep() {
  try {
    const s = await api.fetch(API.infraPmSweepRun(), { method: 'POST', body: JSON.stringify({}) });
    flash(`PM sweep done — ${s.raised || 0} WOs, ${s.groups_created || 0} groups, ${s.tardy || 0} tardy, ${s.skipped_existing || 0} already covered`, 'success');
    await loadSchedules();
  } catch (e) { flash(e.message || 'Sweep failed', 'error'); }
}

// --- Work groups ---------------------------------------------------------- //

async function loadWorkGroups() {
  try {
    workGroups = await api.fetch(API.infraWorkGroups(), { method: 'GET' }) || [];
  } catch (e) { flash(e.message || 'Failed to load work groups', 'error'); return; }
  renderWorkGroups();
}

const WG_BADGE = { open: 'info', in_progress: 'warn', completed: 'ok', cancelled: 'off' };

function renderWorkGroups() {
  if (!workGroups.length) {
    cel.wgTbody.innerHTML = '<tr><td colspan="4" style="color:var(--sub);">No work groups.</td></tr>';
    return;
  }
  cel.wgTbody.innerHTML = workGroups.map(g => `<tr>
    <td><strong>${escapeHtml(g.subject)}</strong>${g.facility_name ? `<div class="table-meta">${escapeHtml(g.facility_name)}</div>` : ''}</td>
    <td>${badge(titleCase(g.status), WG_BADGE[g.status] || 'off')}</td>
    <td style="text-align:center;">${g.wo_done}/${g.wo_total}</td>
    <td><button class="btn ghost" data-wg-open="${escapeHtml(g.id)}">Open</button></td>
  </tr>`).join('');
}

const WO_STATUS_BADGE = { planned: 'off', scheduled: 'off', assigned: 'info', in_progress: 'warn',
  completed: 'ok', verified: 'ok', closed: 'off', cancelled: 'off' };

async function openWorkGroup(id) {
  await ensureTechnicians();
  let g;
  try { g = await api.fetch(API.infraWorkGroup(id), { method: 'GET' }); }
  catch (e) { flash(e.message || 'Failed to load', 'error'); return; }
  const teamIds = new Set((g.supervisors || []).map(s => String(s.technician_id)));
  cel.wgDetail.innerHTML = `
    <div style="display:flex; justify-content:space-between; align-items:baseline;">
      <strong style="font-size:14px;">${escapeHtml(g.subject)}</strong>
      ${badge(titleCase(g.status), WG_BADGE[g.status] || 'off')}
    </div>
    <div style="color:var(--sub); margin:2px 0 10px;">${escapeHtml(g.work_set_name || '')}${g.facility_name ? ' · ' + escapeHtml(g.facility_name) : ''} · ${g.wo_done}/${g.wo_total} done</div>
    <div class="label" style="font-size:12px; color:var(--sub); margin-bottom:4px;">Work orders</div>
    <div class="table-wrap"><table style="font-size:13px;"><tbody>
      ${(g.work_orders || []).map(w => `<tr><td>${escapeHtml(w.subject)}</td><td>${badge(titleCase(w.status), WO_STATUS_BADGE[w.status] || 'off')}</td></tr>`).join('') || '<tr><td style="color:var(--sub);">No work orders.</td></tr>'}
    </tbody></table></div>
    <div class="label" style="font-size:12px; color:var(--sub); margin:10px 0 4px;">Supervisor team</div>
    <div id="opsWgTeamList" style="max-height:140px; overflow:auto; border:1px solid var(--border-soft); border-radius:6px; padding:6px;">
      ${pmTechnicians.map(tc => `<label style="display:block; padding:2px 0;"><input type="checkbox" data-wg-sup value="${escapeHtml(tc.id)}"${teamIds.has(String(tc.id)) ? ' checked' : ''}> ${escapeHtml(tc.full_name || tc.id)}</label>`).join('') || '<span style="color:var(--sub);">No technicians.</span>'}
    </div>
    <div style="margin-top:8px;"><button class="btn primary" id="opsWgSaveTeam" data-wg-id="${escapeHtml(g.id)}">Save team</button></div>`;
  const saveBtn = document.getElementById('opsWgSaveTeam');
  saveBtn?.addEventListener('click', () => saveWorkGroupTeam(g.id));
}

async function saveWorkGroupTeam(id) {
  const ids = Array.from(cel.wgDetail.querySelectorAll('input[data-wg-sup]:checked')).map(c => c.value);
  try {
    await api.fetch(API.infraWorkGroupTeam(id), { method: 'PATCH', body: JSON.stringify({ technician_ids: ids }) });
    flash('Team updated', 'success');
    await loadWorkGroups();
    await openWorkGroup(id);
  } catch (e) { flash(e.message || 'Save failed', 'error'); }
}

export default { init, initTeam, initFacilities, initCalendar };
